import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import JobSearch from './JobSearch';
import FeaturedJobs from './FeaturedJobs';
import Footer from './Footer';
import Modal from './Modal';
import ApplyNowModal from './ApplyNowModal';
import ReferCandidateCard from './ReferCandidateCard';
import JobDetailsModal from './JobDetailsModal';
import ReferEarnModal from './ReferEarnModal';
import LocationPreferenceDialog from './LocationPreferenceDialog';
import RadiusExpansionPrompt, { NoMoreJobsMessage } from './RadiusExpansionPrompt';

import { searchJobs, getCurrentLocation, getDefaultLocation, getLocationResults, fetchPlaceIdDetails, saveCandidateLocation, getTotalUnreadNotificationCount, fetchJobsFilterOptions, getCandidateByMobile, getCityNameFromCoordinates, getLocationDetailsFromCoordinates, getLocationFromIP, fetchCandidateSkills, fetchInactiveCandidateSkillsWithoutFallback, fetchAllActiveSkills } from '../services/apiService';
import { getCandidateId } from '../services/authService';
import TranslationUtils from '../utils/translationUtils';
import { isWithinRadius, LOCATION_PREFERENCE_SHOWN_KEY, LOCATION_PREFERENCE_KEY } from '../utils/locationUtils';

// Location cache utilities
const LOCATION_CACHE_KEY = 'geocode_cache';
const CACHE_EXPIRY_HOURS = 24;


// Programmatic token init is centralized in App via initProgrammaticToken

const getCachedLocation = (cityName) => {
    try {
        const cache = JSON.parse(localStorage.getItem(LOCATION_CACHE_KEY) || '{}');
        const cityKey = cityName.toLowerCase().trim();
        const cached = cache[cityKey];
        
        if (cached && cached.timestamp) {
            const now = Date.now();
            const cacheAge = now - cached.timestamp;
            const maxAge = CACHE_EXPIRY_HOURS * 60 * 60 * 1000;
            
            if (cacheAge < maxAge) {
                console.log('[Homepage] 🎯 Cache HIT for city:', cityName, 'Coords:', cached.latitude, cached.longitude);
                return { latitude: cached.latitude, longitude: cached.longitude };
            } else {
                console.log('[Homepage] ⏰ Cache EXPIRED for city:', cityName, 'Age:', Math.round(cacheAge / (60 * 60 * 1000)), 'hours');
            }
        } else {
            console.log('[Homepage] 🚫 Cache MISS for city:', cityName);
        }
        return null;
    } catch (error) {
        console.warn('[Homepage] Error reading location cache:', error);
        return null;
    }
};

const cacheLocation = (cityName, locationData) => {
    try {
        const cache = JSON.parse(localStorage.getItem(LOCATION_CACHE_KEY) || '{}');
        const cityKey = cityName.toLowerCase().trim();
        
        cache[cityKey] = {
            latitude: locationData.latitude,
            longitude: locationData.longitude,
            timestamp: Date.now()
        };
        
        localStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify(cache));
        console.log('[Homepage] 💾 CACHED location for', cityName, '→', locationData.latitude, locationData.longitude);
    } catch (error) {
        console.warn('[Homepage] Error caching location:', error);
    }
};


const Homepage = () => {
    const { isAuthenticated, showSignIn, currentUser } = useAuth();
    const { selectedLanguage } = useLanguage();
    const { showToast } = useToast();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedJob, setSelectedJob] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [apiBusy, setApiBusy] = useState(true); // true whenever a jobs request is in-flight
    const [loadingMore, setLoadingMore] = useState(false); // true when loading more jobs (pagination)
    const [error, setError] = useState(null);
    const [hasInitialLoadCompleted, setHasInitialLoadCompleted] = useState(false);
    const [initialFetchDone, setInitialFetchDone] = useState(false); // gates empty state until first API completes
    const [location, setLocation] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMoreJobs, setHasMoreJobs] = useState(true);
    const [mobileNumber, setMobileNumber] = useState(null);
    const [isJobDetailsModalOpen, setIsJobDetailsModalOpen] = useState(false);
    const [selectedJobForDetails, setSelectedJobForDetails] = useState(null);
    const [showMobileModal, setShowMobileModal] = useState(false);

    const [tempMobileNumber, setTempMobileNumber] = useState('');
    const [tempMobileError, setTempMobileError] = useState('');
    const [selectedCountry, setSelectedCountry] = useState('+91');
    const [isReferModalOpen, setIsReferModalOpen] = useState(false);
    const [selectedJobForRefer, setSelectedJobForRefer] = useState(null);
    
    // Location preference dialog state
    const [showLocationPreferenceDialog, setShowLocationPreferenceDialog] = useState(false);
    const [savedLocation, setSavedLocation] = useState(null);
    const [actualJobLocation, setActualJobLocation] = useState(null); // Track the actual location used for API calls
    
    
    // Search functionality state
    const [allJobs, setAllJobs] = useState([]); // Store all jobs for filtering
    const [isSearchActive, setIsSearchActive] = useState(false);
    const [isServerFiltered, setIsServerFiltered] = useState(false);
    // Prevent duplicate initial reload and concurrent fetches
    const skipNextLanguageReload = useRef(true);
    const isLoadingJobsRef = useRef(false);
    const isSearchingRef = useRef(false);
    const locationSaveInProgressRef = useRef(false); // Prevent duplicate location saves

    // City filter (Homepage) - single location selection only
    const [selectedCity, setSelectedCity] = useState(''); // single string
    const [selectedCityCoord, setSelectedCityCoord] = useState(null); // single { name, latitude, longitude }
    const [cityQuery, setCityQuery] = useState('');
    const [showCityDropdown, setShowCityDropdown] = useState(false);
    const cityInputRef = useRef(null);
    const [serverCityOptions, setServerCityOptions] = useState([]);
    const [isCityLoading, setIsCityLoading] = useState(false);
    const [isAddingCity, setIsAddingCity] = useState(false);
    // Skills list for dropdown - now using filterOptions.skills instead of separate API
    // Selected skills chips (client-side filter and UI)
    const [selectedSkills, setSelectedSkills] = useState([]); // array of strings
    // Selected designations (job titles) chips
    const [selectedDesignations, setSelectedDesignations] = useState([]); // array of strings
    // Selected keywords (free-text search terms for job description search)
    const [selectedKeywords, setSelectedKeywords] = useState([]); // array of strings
    // Filter options from API
    const [filterOptions, setFilterOptions] = useState({
        designations: [],
        locations: [],
        skills: []
    });
    
    // Candidate skills for highlighting
    const [candidateSkills, setCandidateSkills] = useState([]);
    // Candidate skills with ratings for partial highlight
    const [candidateSkillsWithRatings, setCandidateSkillsWithRatings] = useState([]);
    // Skill ID to name mapping for budget skill matching
    const [skillIdToNameMap, setSkillIdToNameMap] = useState({});
    
    // Fetch candidate skills for highlighting when authenticated
    useEffect(() => {
        const fetchSkillsForHighlighting = async () => {
            if (!isAuthenticated) {
                setCandidateSkills([]);
                return;
            }
            
            try {
                console.log('[Homepage] Fetching candidate skills for highlighting...');
                
                // Step 1: Fetch all active skills to build ID-to-name mapping
                const allSkillsResponse = await fetchAllActiveSkills();
                const skillIdToName = {};
                
                console.log('[Homepage] All active skills response:', allSkillsResponse);
                
                if (allSkillsResponse.success && allSkillsResponse.data) {
                    allSkillsResponse.data.forEach(skill => {
                        if (typeof skill === 'object') {
                            const skillId = skill.skillId || skill.id || skill.skill_id;
                            const skillName = skill.skillName || skill.name || skill.skill_name;
                            if (skillId && skillName) {
                                skillIdToName[skillId] = skillName;
                            }
                        } else if (typeof skill === 'string') {
                            // If it's just a string, use it as both ID and name
                            skillIdToName[skill] = skill;
                        }
                    });
                    console.log('[Homepage] Skill ID to Name mapping:', skillIdToName);
                    // Store the mapping in state for JobCard to use
                    setSkillIdToNameMap(skillIdToName);
                }
                
                // Step 2: Fetch candidate's selected skill IDs (active skills)
                const activeSkillsResponse = await fetchCandidateSkills();
                const activeSkillNames = [];
                const activeSkillsWithRatings = [];
                
                console.log('[Homepage] Active skills response:', activeSkillsResponse);
                
                if (activeSkillsResponse.success && activeSkillsResponse.data) {
                    console.log('[Homepage] Processing active skills data:', activeSkillsResponse.data);
                    activeSkillsResponse.data.forEach(skillData => {
                        // Handle different response formats
                        let skillId = null;
                        let skillName = null;
                        let rating = null;
                        
                        if (typeof skillData === 'object') {
                            skillId = skillData.skillId || skillData.id || skillData.skill_id;
                            skillName = skillData.skillName || skillData.name || skillData.skill_name;
                            rating = skillData.rating || skillData.skillRating || 0;
                        } else if (typeof skillData === 'string' || typeof skillData === 'number') {
                            skillId = skillData;
                        }
                        
                        // Try to get skill name from mapping
                        if (skillId && skillIdToName[skillId]) {
                            const mappedName = skillIdToName[skillId];
                            console.log(`[Homepage] Mapped skill ID ${skillId} to name: ${mappedName}, rating: ${rating}`);
                            activeSkillNames.push(mappedName);
                            activeSkillsWithRatings.push({
                                skillName: mappedName,
                                skillId: skillId,
                                rating: rating
                            });
                        } else if (skillName) {
                            // If we have a direct skill name, use it
                            console.log(`[Homepage] Using direct skill name: ${skillName}, rating: ${rating}`);
                            activeSkillNames.push(skillName);
                            activeSkillsWithRatings.push({
                                skillName: skillName,
                                skillId: skillId,
                                rating: rating
                            });
                        } else {
                            console.warn(`[Homepage] Could not map skill:`, skillData, 'skillId:', skillId);
                        }
                    });
                }
                
                // Step 3: Fetch inactive skills WITHOUT fallback (for accurate skill highlighting)
                const inactiveSkillsResponse = await fetchInactiveCandidateSkillsWithoutFallback();
                const inactiveSkillNames = [];
                const inactiveSkillsWithRatings = [];
                
                if (inactiveSkillsResponse.success && inactiveSkillsResponse.data) {
                    inactiveSkillsResponse.data.forEach(skill => {
                        if (skill.skillName) {
                            inactiveSkillNames.push(skill.skillName);
                            inactiveSkillsWithRatings.push({
                                skillName: skill.skillName,
                                skillId: skill.skillId,
                                rating: skill.rating || skill.skillRating || 0
                            });
                            // Add to mapping if not present
                            if (skill.skillId && !skillIdToName[skill.skillId]) {
                                skillIdToName[skill.skillId] = skill.skillName;
                            }
                        }
                    });
                }
                
                // Combine active and inactive skills (remove duplicates)
                const allSkills = [...new Set([...activeSkillNames, ...inactiveSkillNames])];
                const allSkillsWithRatings = [...activeSkillsWithRatings, ...inactiveSkillsWithRatings];
                
                console.log('[Homepage] Candidate skills fetched:', {
                    active: activeSkillNames.length,
                    activeSkills: activeSkillNames,
                    inactive: inactiveSkillNames.length,
                    inactiveSkills: inactiveSkillNames,
                    total: allSkills.length,
                    allSkills: allSkills,
                    skillsWithRatings: allSkillsWithRatings
                });
                
                setCandidateSkills(allSkills);
                setCandidateSkillsWithRatings(allSkillsWithRatings);
            } catch (error) {
                console.error('[Homepage] Error fetching candidate skills:', error);
            }
        };
        
        fetchSkillsForHighlighting();
    }, [isAuthenticated]);

    // ========== RADIUS-BASED FETCHING STATE ==========
    const [currentRadius, setCurrentRadius] = useState(50); // Start with 50km
    const radiusLevels = [50, 100, 150, 200, 250];
    const [currentRadiusIndex, setCurrentRadiusIndex] = useState(0);
    const [fetchedJobIds, setFetchedJobIds] = useState(new Set()); // Track fetched job IDs to prevent duplicates
    const [hasMoreRadiusLevels, setHasMoreRadiusLevels] = useState(true);
    const [isExpandingRadius, setIsExpandingRadius] = useState(false);
    const isRadiusBasedMode = true; // Always use radius-based fetching
    const [hasCompletedCurrentRadiusFetch, setHasCompletedCurrentRadiusFetch] = useState(false);
    
    // Active filters for radius expansion (to maintain filters when expanding radius)
    const [activeFilters, setActiveFilters] = useState({ skillIds: [], designationIds: [], keywords: [] });
    
    // Lazy loading state for radius-based fetching
    const [jobsBuffer, setJobsBuffer] = useState([]); // Buffer holding all fetched jobs for current radius
    const [displayedJobCount, setDisplayedJobCount] = useState(0); // Number of jobs currently displayed
    const batchSize = 10; // Display 10 jobs at a time
    const [isLoadingNextBatch, setIsLoadingNextBatch] = useState(false);
    
    // Getters for radius expansion
    const canExpandRadius = currentRadiusIndex < radiusLevels.length - 1;
    const nextRadius = canExpandRadius ? radiusLevels[currentRadiusIndex + 1] : null;
    const promptCurrentRadius = isExpandingRadius
        ? radiusLevels[Math.max(currentRadiusIndex - 1, 0)]
        : currentRadius;
    const hasMoreJobsInBuffer = displayedJobCount < jobsBuffer.length;
    const allJobsInRadiusDisplayed = displayedJobCount >= jobsBuffer.length && hasCompletedCurrentRadiusFetch;
    // ========== END RADIUS-BASED FETCHING STATE ==========

    // Disabled: Do not auto-trigger server filter when chips change.
    // The filters API must only run when the user clicks "Search Jobs".
    useEffect(() => {
        // Intentionally left blank to avoid auto-triggering fetchJobsWithFilters
        // on chip changes.
    }, [selectedCity, selectedCityCoord, selectedSkills, selectedDesignations]);

    // Filter options are now fetched in loadJobs() to avoid duplicate API calls
    // This ensures filter options use the same coordinates as the jobs API

    // Skills chip handlers
    const addSkill = (skillLabel) => {
        const val = String(skillLabel || '').trim();
        if (!val) return;
        setSelectedSkills(prev => (prev.includes(val) ? prev : [...prev, val]));
    };    

    const removeSkill = (skillLabel) => {
        setSelectedSkills(prev => prev.filter(s => s !== skillLabel));
    };

    const clearAllSkills = () => {
        setSelectedSkills([]);
    };

    // Designation chip handlers
    const addDesignation = (label) => {
        const val = String(label || '').trim();
        if (!val) return;
        setSelectedDesignations(prev => (prev.includes(val) ? prev : [...prev, val]));
    };

    const removeDesignation = (label) => {
        setSelectedDesignations(prev => prev.filter(s => s !== label));
    };

    const clearAllDesignations = () => {
        setSelectedDesignations([]);
    };

    // Removed legacy cityOptions derived from jobs; we now rely solely on API suggestions

    // Debounced fetch of server-side city suggestions (only after 3+ chars)
    useEffect(() => {
        const q = cityQuery.trim();
        // Do not call API until user has typed at least 3 characters
        if (!q || q.length < 3) {
            setIsCityLoading(false);
            setServerCityOptions([]);
            return;
        }
        const handle = setTimeout(async () => {
            setIsCityLoading(true);
            try {
                const resp = await getLocationResults(q);
                if (resp?.success) {
                    const arr = Array.isArray(resp.data) ? resp.data : [];
                    setServerCityOptions(arr);
                } else {
                    setServerCityOptions([]);
                }
            } catch (e) {
                setServerCityOptions([]);
            } finally {
                setIsCityLoading(false);
            }
        }, 350);
        return () => clearTimeout(handle);
    }, [cityQuery]);

    // Removed mergedCityOptions: dropdown should show only API-provided options (serverCityOptions)

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (!cityInputRef.current) return;
            const parent = cityInputRef.current.parentElement;
            if (parent && !parent.contains(e.target)) setShowCityDropdown(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const addCity = async (city) => {
        // city must come from API suggestions; add chip only after coords are known
        const label = typeof city === 'object' && city !== null ? (city.label || city.name || city.description || '') : String(city || '');
        const placeId = typeof city === 'object' && city !== null ? (city.placeId || city.place_id || '') : '';
        const val = label.trim();
        if (!val) return;

        setCityQuery('');
        setShowCityDropdown(false);

        try {
            setIsAddingCity(true);
            // Require placeId for precise coordinates
            if (placeId) {
                const resp = await fetchPlaceIdDetails(placeId);
                if (resp?.success && resp.data) {
                    const coords = { latitude: Number(resp.data.latitude), longitude: Number(resp.data.longitude) };
                    cacheLocation(val, coords);
                    const entry = { name: val, ...coords };
                    setSelectedCity(val);
                    setSelectedCityCoord(entry);
                    
                    // Call filter options API with selected city coordinates
                    try {
                        const filterData = await fetchJobsFilterOptions(
                            coords.latitude,
                            coords.longitude,
                            150 // 150km radius
                        );
                        if (filterData) {
                            setFilterOptions({
                                designations: filterData.designations || [],
                                locations: filterData.locations || [],
                                skills: filterData.skills || []
                            });
                            console.log('[Homepage] Filter options updated for selected city:', val, filterData);
                        }
                    } catch (e) {
                        console.warn('[Homepage] Filter options fetch failed for selected city:', e);
                    }
                    
                    // Save candidate location if user is logged in
                    if (isAuthenticated && mobileNumber && mobileNumber !== '0') {
                        try {
                            console.log('[Homepage] Saving candidate location from search...');
                            // Extract pincode from place details response if available
                            const pincode = resp.data.pincode || '';
                            console.log('[Homepage] Place details pincode:', pincode);
                            const saveResult = await saveCandidateLocation(
                                coords.latitude,
                                coords.longitude,
                                pincode
                            );
                            if (saveResult.success) {
                                console.log('[Homepage] Candidate location saved successfully from search with pincode:', pincode);
                            } else {
                                console.warn('[Homepage] Failed to save candidate location from search:', saveResult.error);
                            }
                        } catch (error) {
                            console.warn('[Homepage] Error saving candidate location from search:', error);
                        }
                    }
                    return;
                } else {
                    showToast && showToast('Unable to fetch city coordinates. Please try another suggestion.', 'warning');
                    return;
                }
            }

            // Try cache as a fallback only if exists
            const cached = getCachedLocation(val);
            if (cached) {
                const entry = { name: val, latitude: Number(cached.latitude), longitude: Number(cached.longitude) };
                setSelectedCity(val);
                setSelectedCityCoord(entry);
                
                // Call filter options API with cached city coordinates
                try {
                    const filterData = await fetchJobsFilterOptions(
                        cached.latitude,
                        cached.longitude,
                        150 // 150km radius
                    );
                    if (filterData) {
                        setFilterOptions({
                            designations: filterData.designations || [],
                            locations: filterData.locations || [],
                            skills: filterData.skills || []
                        });
                        console.log('[Homepage] Filter options updated for cached city:', val, filterData);
                    }
                } catch (e) {
                    console.warn('[Homepage] Filter options fetch failed for cached city:', e);
                }
                return;
            }

            // Do not add chip without coordinates
            showToast && showToast('Please choose a city from suggestions so we can get coordinates.', 'info');
        } catch (e) {
            console.warn('[Homepage] addCity failed for', val, e);
            showToast && showToast('Failed to add city. Please try again.', 'error');
        } finally {
            setIsAddingCity(false);
        }
    };

    // Clear all filters (chips) and reload jobs (default list)
    const clearAllFilters = () => {
        setSelectedCity('');
        setSelectedCityCoord(null);
        setCityQuery('');
        setSelectedSkills([]);
        setSelectedDesignations([]);
        setSelectedKeywords([]);
        setIsSearchActive(false);
        setIsServerFiltered(false);
        // Also clear persisted city chips so they don't reappear after reload
        try {
            localStorage.removeItem('selected_city_coords');
        } catch (_) {}
        // reload default jobs
        loadJobs(1, true);
    };

    // Keyword management functions
    const addKeyword = (keyword) => {
        const trimmed = keyword.trim();
        if (trimmed && !selectedKeywords.includes(trimmed)) {
            setSelectedKeywords(prev => [...prev, trimmed]);
        }
    };

    const removeKeyword = (keyword) => {
        setSelectedKeywords(prev => prev.filter(k => k !== keyword));
    };

    const clearAllKeywords = () => {
        setSelectedKeywords([]);
    };

    const removeCity = (city) => {
        if (selectedCity === city) {
            setSelectedCity('');
            setSelectedCityCoord(null);
        }
    };

    const clearAllCities = () => {
        setSelectedCity('');
        setSelectedCityCoord(null);
        localStorage.removeItem('selected_city_coords');
    };

    // Do not persist or restore city chips across reloads; proactively clear legacy storage on mount
    useEffect(() => {
        try { localStorage.removeItem('selected_city_coords'); } catch (_) {}
    }, []);

    // Fetch unread notification count once on homepage load and cache it (guard against StrictMode)
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const key = 'unread_count_fetched_at';
                const now = Date.now();
                let lastTs = 0;
                try { lastTs = parseInt(sessionStorage.getItem(key) || '0', 10) || 0; } catch (_) {}
                const TTL_MS = 10000; // 10s coalescing
                if (lastTs && (now - lastTs) <= TTL_MS) return;
                try { sessionStorage.setItem(key, String(now)); } catch (_) {}
                const cid = (typeof getCandidateId === 'function') ? getCandidateId() : null;
                if (!cid) return;
                const res = await getTotalUnreadNotificationCount(cid);
                if (!cancelled && res?.success) {
                    try { localStorage.setItem('unreadCount', String(res.data || 0)); } catch (_) {}
                    console.log('[Homepage] Cached unread notification count:', res.data || 0);
                }
            } catch (_) { /* silent */ }
        })();
        return () => { cancelled = true; };
    }, []);

    // Apply city filter to jobs list (client-side) ONLY during an active search
    // AND when we are NOT using server-filtered results to avoid double-filtering.
    useEffect(() => {
        // Don't interfere with radius-based display logic (both normal and filtered searches)
        // Radius-based mode handles its own batch display via jobsBuffer
        if (isRadiusBasedMode) {
            return;  // ✅ Skip for all radius-based searches (normal + filtered)
        }
        
        if (!isSearchActive || isServerFiltered) {
            setJobs(allJobs);
            return;
        }
        if (!selectedCity || selectedCity.trim() === '') {
            setJobs(allJobs);
            return;
        }
        const cityFilter = selectedCity.toLowerCase();
        const filtered = allJobs.filter(j => {
            const cityField = (j.cityName || j.city || j.location || '').toString().toLowerCase();
            return cityField.includes(cityFilter);
        });
        setJobs(filtered);
    }, [selectedCity, allJobs, isSearchActive, isServerFiltered, isRadiusBasedMode]);

    // Initialize mobile number from authenticated user or localStorage
    useEffect(() => {
        let userMobile = null;
        
        if (isAuthenticated && currentUser?.mobileNumber) {
            userMobile = currentUser.mobileNumber;
        } else {
            // Try to get from localStorage
            userMobile = localStorage.getItem('userMobileNumber');
        }
        
        if (userMobile) {
            setMobileNumber(userMobile);
            console.log('[Homepage] Using mobile number:', userMobile);
        } else {
            // Fallback to default mobile number for demo
            const defaultMobile = '9876543210';
            setMobileNumber(defaultMobile);
            console.log('[Homepage] Using default mobile number:', defaultMobile);
        }
    }, [isAuthenticated, currentUser]);

    // Location preference dialog is handled entirely by checkLocationPreference function
    // which is called during loadJobs and handles all validation including distance check

    const handleQuickApply = (job) => {
        if (!isAuthenticated) {
            showSignIn();
            return;
        }
        // User is authenticated, open Apply Now modal
        setSelectedJob(job);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedJob(null);
    };

    // Refresh jobs after a successful application from ApplyNowModal
    const handleApplySuccess = () => {
        try {
            // Clear TTL guard and cache so the next call actually refetches
            sessionStorage.removeItem('homepage_jobs_fetched_at');
            sessionStorage.removeItem('homepage_jobs_cache');
        } catch (_) {}
        // Also refresh unread notification count immediately
        (async () => {
            try {
                const cid = (typeof getCandidateId === 'function') ? getCandidateId() : null;
                if (cid) {
                    const res = await getTotalUnreadNotificationCount(cid);
                    if (res?.success) {
                        try { localStorage.setItem('unreadCount', String(res.data || 0)); } catch (_) {}
                        // Notify listeners (e.g., Header) to update badge immediately
                        try { window.dispatchEvent(new CustomEvent('unreadCountUpdated', { detail: { count: Number(res.data || 0) } })); } catch (_) {}
                    }
                }
            } catch (_) { /* silent */ }
        })();
        // Trigger a fresh reload of jobs
        if (mobileNumber) {
            loadJobs(1, true, mobileNumber);
        } else {
            loadJobs(1, true);
        }
    };

    // JobDetailsModal handlers
    const handleViewDetails = (job) => {
        // Mark that this job was opened directly from the list
        const decorated = { ...job, _fromList: true };
        setSelectedJobForDetails(decorated);
        setIsJobDetailsModalOpen(true);
    };

    const closeJobDetailsModal = () => {
        setIsJobDetailsModalOpen(false);
        setSelectedJobForDetails(null);
    };

    // Removed legacy handleJobApply to avoid duplicate application submissions and alert popups.

    const handleJobRefer = (job) => {
        if (!isAuthenticated) {
            showSignIn();
            return;
        }
        // User is authenticated, open Refer & Earn modal
        setSelectedJobForRefer(job);
        setIsReferModalOpen(true);
    };

    const closeReferModal = () => {
        setIsReferModalOpen(false);
        setSelectedJobForRefer(null);
    };

    // Handle search functionality
    const handleSearch = async (params) => {
        // Simple server-filtered search when there are terms or chips; otherwise client list
        try {
            // Prevent overlap with coordinate-based loader
            if (isLoadingJobsRef.current) {
                console.log('[Homepage] handleSearch skipped: loadJobs in-flight');
                return;
            }
            const overrideCities = Array.isArray(params?._overrideSelectedCities) ? params._overrideSelectedCities : null;
            const overrideCityCoords = Array.isArray(params?._overrideSelectedCityCoords) ? params._overrideSelectedCityCoords : null;

            // Build designation payload from chips
            const designationsFromChips = (selectedDesignations || []).map(s => String(s).trim()).filter(Boolean);
            
            // Build skills payload from chips
            const skillsFromChips = (selectedSkills || []).map(s => String(s).trim()).filter(Boolean);
            
            // Build keywords payload (free-text search terms)
            const keywordsPayload = (selectedKeywords || []).map(s => String(s).trim()).filter(Boolean);

            const effSelectedCity = overrideCities ?? selectedCity;
            const effSelectedCityCoord = overrideCityCoords ?? selectedCityCoord;
            const hasSearchTerms = Boolean(
                designationsFromChips.length > 0 || 
                skillsFromChips.length > 0 || 
                keywordsPayload.length > 0 ||
                (effSelectedCity && effSelectedCity.trim() !== '') || 
                effSelectedCityCoord
            );
            setIsSearchActive(hasSearchTerms);

            if (!hasSearchTerms) {
                // No search -> show existing list
                setJobs(allJobs);
                setIsServerFiltered(false);
                setLoading(false);
                setApiBusy(false);
                return;
            }

            if (isSearchingRef.current) return;
            isSearchingRef.current = true;
            setLoading(true);
            setApiBusy(true);

            // Map skill labels to IDs expected by backend
            const skillMap = new Map(
                (Array.isArray(filterOptions?.skills) ? filterOptions.skills : [])
                    .map(s => [String(s.label || s.name || s.title || '').trim().toLowerCase(), Number(s.id)])
            );
            const skillIdsPayload = Array.from(new Set(skillsFromChips
                .map(lbl => skillMap.get(String(lbl).toLowerCase()))
                .filter(id => Number.isInteger(id))));

            // Map designation labels to IDs expected by backend
            const designationMap = new Map(
                (Array.isArray(filterOptions?.designations) ? filterOptions.designations : [])
                    .map(d => [String(d.label || d.name || d.title || '').trim().toLowerCase(), Number(d.id)])
            );
            const designationIdsPayload = Array.from(new Set(designationsFromChips
                .map(lbl => designationMap.get(String(lbl).toLowerCase()))
                .filter(id => Number.isInteger(id))));

            // Remove the old multiple locations logic since we now use single location
            // The location is handled directly in the searchJobs call below

            // Clear current list so shimmer skeletons appear during fetch
            setJobs([]);
            setAllJobs([]);
            setInitialFetchDone(false);

            // Use the selected city coordinate for the unified search (single location requirement)
            const searchLat = effSelectedCityCoord?.latitude || location?.latitude || 28.6139;
            const searchLng = effSelectedCityCoord?.longitude || location?.longitude || 77.2090;
            const searchCity = effSelectedCityCoord?.name || location?.city || 'your location';
            
            // Update the actual job location for header display
            setActualJobLocation({
                latitude: searchLat,
                longitude: searchLng,
                city: searchCity
            });
            
            // Check if this is an actual filtered search or just location change
            const hasActualFilters = (skillIdsPayload && skillIdsPayload.length > 0) || 
                                    (designationIdsPayload && designationIdsPayload.length > 0) ||
                                    (keywordsPayload && keywordsPayload.length > 0);
            
            if (hasActualFilters) {
                // Store active filters for radius expansion (only if actual filters applied)
                const filters = {
                    skillIds: skillIdsPayload,
                    designationIds: designationIdsPayload,
                    keywords: keywordsPayload
                };
                setActiveFilters(filters);
                console.log('[Homepage] 🔍 Storing filters for radius expansion:', filters);
            } else {
                // Clear filters for location-only search
                setActiveFilters({ skillIds: [], designationIds: [], keywords: [] });
                console.log('[Homepage] 🔍 Location-only search, clearing filters');
            }
            
            // Reset radius state for fresh search
            console.log('[Homepage] 🔄 Resetting radius state for fresh search');
            resetRadius();
            
            // Use radius-based fetching with or without filters
            // Always start with 50km for fresh search (don't rely on state due to async updates)
            const startingRadius = 50;
            console.log('[Homepage] 🔍 Search with radius-based fetching');
            console.log('[Homepage] 🔍 Starting radius:', startingRadius, 'km');
            console.log('[Homepage] 🔍 Has filters:', hasActualFilters);
            console.log('[Homepage] 🔍 Skill IDs:', skillIdsPayload);
            console.log('[Homepage] 🔍 Designation IDs:', designationIdsPayload);
            console.log('[Homepage] 🔍 Keywords:', keywordsPayload);
            
            const response = await searchJobs({
                latitude: searchLat,
                longitude: searchLng,
                distance: startingRadius, // ✅ Always start with 50km for filtered search
                skillIds: skillIdsPayload,
                designationIds: designationIdsPayload,
                keywords: keywordsPayload, // ✅ Pass keywords for job description search
                page: 1,
                size: 1000, // ✅ Fetch all jobs within radius for client-side batching
                mobileNumber: mobileNumber || localStorage.getItem('userMobileNumber') || '0',
            });

            let finalJobs = [];
            if (response?.success && response.data) {
                const newJobs = response.data.jobs || response.data || [];
                console.log('[Homepage] 🔍 Fetched', newJobs.length, 'filtered jobs from API');
                
                const transformedJobs = newJobs.map(job => {
                    let skills = job.skills || job.tags || job.requirements || 'General';
                    if (!skills || (typeof skills !== 'string' && !Array.isArray(skills))) {
                        skills = 'General';
                    }
                    // Use positionId as primary ID (most stable field)
                    const stableId = job.positionId || job.budgetCityId || job.id || job.jobId || Math.random().toString(36);
                    return {
                        ...job,
                        id: stableId,
                        skills,
                        title: job.jobName || job.title || 'Job Title Not Available',
                        location: job.cityName && job.pincode ? `${job.cityName} - ${job.pincode}` : 'Location Not Specified',
                        salary: job.maxSalaryLimit ? `Up to ₹${job.maxSalaryLimit.toLocaleString()} per month` : 'Salary Not Disclosed'
                    };
                });

                const currentLanguage = selectedLanguage?.code || 'en';
                finalJobs = transformedJobs;
                if (TranslationUtils.isTranslationNeeded(currentLanguage)) {
                    finalJobs = await TranslationUtils.translateJobFields(
                        transformedJobs,
                        currentLanguage,
                        ['jobName', 'jobDetails', 'cityName']
                    );
                }

                // Store all filtered jobs in buffer and display first batch
                setJobsBuffer(finalJobs);
                setAllJobs(finalJobs);
                setDisplayedJobCount(0);
                
                // Display first batch (up to 10 jobs)
                const initialBatchSize = finalJobs.length < batchSize ? finalJobs.length : batchSize;
                setJobs(finalJobs.slice(0, initialBatchSize));
                setDisplayedJobCount(initialBatchSize);
                
                console.log('[Homepage] 🔍 Filter results: Buffered', finalJobs.length, 'jobs, displaying first', initialBatchSize, 'jobs');
                
                // Track job IDs for duplicate prevention during radius expansion
                const newFetchedJobIds = new Set();
                finalJobs.forEach(job => {
                    newFetchedJobIds.add(job.id);
                });
                setFetchedJobIds(newFetchedJobIds);
                console.log('[Homepage] 🔍 Tracking', newFetchedJobIds.size, 'job IDs for duplicate prevention');
                
                // Only set isServerFiltered if actual filters (skills/designations) are applied
                // Location change alone is NOT a filtered search
                const hasActualFilters = (skillIdsPayload && skillIdsPayload.length > 0) || 
                                        (designationIdsPayload && designationIdsPayload.length > 0);
                setIsServerFiltered(hasActualFilters);
                console.log('[Homepage] 🔍 Has actual filters:', hasActualFilters, { skillIds: skillIdsPayload.length, designationIds: designationIdsPayload.length });
                
                // Mark current radius fetch as complete for radius expansion prompt
                setHasCompletedCurrentRadiusFetch(true);
            } else {
                // On failure, do not change list; just reset flags
                setIsServerFiltered(false);
            }

            setLoading(false);
            setApiBusy(false);
            setHasInitialLoadCompleted(true);
            setInitialFetchDone(true);
        } catch (err) {
            console.error('[Homepage] handleSearch (server-filter) error:', err);
            setIsServerFiltered(false);
            setLoading(false);
            setApiBusy(false);
            setHasInitialLoadCompleted(true);
            setInitialFetchDone(true);
        } finally {
            isSearchingRef.current = false;
        }
    };

    /**
     * Get saved location for the user (without showing preference dialog)
     * The preference dialog is now handled in initializeApp
     */
    const checkLocationPreference = useCallback(async (currentLocation) => {
        console.log('[Homepage] checkLocationPreference called - fetching saved location only');
        
        // Only check location preference if user has a valid mobile number
        const mobile = mobileNumber || localStorage.getItem('userMobileNumber');
        console.log('[Homepage] Mobile number for location check:', mobile);
        
        if (!mobile || mobile === '0') {
            console.log('[Homepage] No valid mobile number, skipping location preference check');
            return null;
        }

        let candidateSavedLocation = null;
        
        try {
            // Fetch candidate data to get saved location
            console.log('[Homepage] Fetching candidate data for saved location...');
            const candidateResult = await getCandidateByMobile();
            
            if (candidateResult.success && candidateResult.data) {
                const candidateData = candidateResult.data;
                candidateSavedLocation = candidateData.currentCity || candidateData.preferredCity;
                
                if (candidateSavedLocation && 
                    candidateSavedLocation.latitude && 
                    candidateSavedLocation.longitude) {
                    
                    console.log('[Homepage] User has valid saved location:', candidateSavedLocation);
                    setSavedLocation(candidateSavedLocation);
                }
            }
        } catch (error) {
            console.warn('[Homepage] Error fetching saved location:', error);
        }
        
        return candidateSavedLocation;
    }, [mobileNumber]);

    /**
     * Load jobs with specific coordinates (bypasses location determination logic)
     */
    const loadJobsWithCoordinates = useCallback(async (coordinates, page = 1, reset = true, userMobile = null) => {
        try {
            console.log('[Homepage] loadJobsWithCoordinates called with:', coordinates);
            
            // Prevent concurrent requests
            if (isLoadingJobsRef.current) {
                console.log('[Homepage] loadJobsWithCoordinates ignored: request already in-flight');
                return;
            }
            
            isLoadingJobsRef.current = true;
            setApiBusy(true);
            console.log('[Homepage] API started with coordinates, apiBusy set to true');
            
            if (reset) {
                setLoading(true);
                setLoadingMore(false);
                setError(null);
                setJobs([]);
                setAllJobs([]);
                setInitialFetchDone(false);
            } else {
                setLoadingMore(true);
            }

            const mobile = userMobile || mobileNumber;
            if (!mobile) {
                throw new Error('Mobile number is required to fetch jobs');
            }

            const lat = Number(coordinates.latitude);
            const lng = Number(coordinates.longitude);

            let resolvedCity = coordinates.city;
            if ((!resolvedCity || String(resolvedCity).trim() === '') && Number.isFinite(lat) && Number.isFinite(lng)) {
                try {
                    resolvedCity = await getCityNameFromCoordinates(lat, lng);
                    console.log('[Homepage] Resolved city name for coordinates:', resolvedCity);
                } catch (resolveError) {
                    console.warn('[Homepage] Failed to resolve city name for coordinates:', resolveError);
                    resolvedCity = 'Your Location';
                }
            }

            const coordinatesWithCity = {
                ...coordinates,
                latitude: lat,
                longitude: lng,
                city: resolvedCity && String(resolvedCity).trim() !== '' ? resolvedCity : 'Your Location'
            };

            console.log('[Homepage] Using coordinates for immediate API call:', { lat, lng, city: coordinatesWithCity.city });

            // Update the actual job location state for the header display
            setActualJobLocation(coordinatesWithCity);

            // Fetch filter options with same coordinates as jobs API
            try {
                const filterData = await fetchJobsFilterOptions(lat, lng, 150);
                if (filterData) {
                    setFilterOptions({
                        designations: filterData.designations || [],
                        locations: filterData.locations || [],
                        skills: filterData.skills || []
                    });
                    console.log('[Homepage] Filter options loaded with coordinates:', filterData);
                }
            } catch (e) {
                console.warn('[Homepage] Filter options fetch failed:', e);
            }

            // Use radius-based fetching instead of old pagination
            if (reset) {
                console.log('[Homepage] 🔄 Resetting radius state for location preference');
                resetRadius();
            }
            
            console.log('[Homepage] 🎯 Using radius-based job fetching for location preference');
            await fetchJobsWithRadius(lat, lng, coordinatesWithCity.city);
        } catch (err) {
            console.error('[Homepage] Error loading jobs with coordinates:', err);
            setError(err.message || 'Failed to load jobs. Please try again.');
        } finally {
            // fetchJobsWithRadius manages its own loading state
            isLoadingJobsRef.current = false;
            setApiBusy(false);
            console.log('[Homepage] API finished with coordinates, apiBusy set to false');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLanguage, mobileNumber, initialFetchDone]);

    /**
     * Handle location preference selection with immediate API call
     */
    const handleLocationPreferenceSelect = async (preference) => {
        console.log('[Homepage] Location preference selected:', preference);
        sessionStorage.setItem(LOCATION_PREFERENCE_KEY, preference);
        setShowLocationPreferenceDialog(false);
        
        // Mark dialog as shown for this session (only after user makes a selection)
        sessionStorage.setItem(LOCATION_PREFERENCE_SHOWN_KEY, 'true');
        
        // Determine coordinates to use immediately
        let coordinatesForAPI;
        if (preference === 'saved' && savedLocation) {
            // Update to saved location city name immediately
            const savedLatitude = Number(savedLocation.latitude);
            const savedLongitude = Number(savedLocation.longitude);
            let savedCityName = savedLocation.cityName || savedLocation.city;

            if ((!savedCityName || savedCityName.trim() === '') && savedLatitude && savedLongitude) {
                try {
                    savedCityName = await getCityNameFromCoordinates(savedLatitude, savedLongitude);
                    console.log('[Homepage] Resolved saved location city name:', savedCityName);
                } catch (error) {
                    console.warn('[Homepage] Failed to resolve saved location city name:', error);
                }
            }

            coordinatesForAPI = {
                latitude: savedLatitude,
                longitude: savedLongitude,
                city: savedCityName && savedCityName.trim() !== '' ? savedCityName : 'Your Saved Location'
            };

            console.log('[Homepage] Using saved location coordinates:', coordinatesForAPI);
        } else if (preference === 'current' && location) {
            // Update to current location city name immediately
            coordinatesForAPI = location;
            console.log('[Homepage] Using current location coordinates:', coordinatesForAPI);
        }
        
        if (coordinatesForAPI) {
            // Update header immediately
            setActualJobLocation(coordinatesForAPI);
            
            // Call API immediately with selected coordinates
            console.log('[Homepage] Calling API immediately with selected coordinates');
            loadJobsWithCoordinates(coordinatesForAPI, 1, true, mobileNumber);
        } else {
            console.warn('[Homepage] No coordinates available for selected preference:', preference);
            // Fallback to regular loadJobs
            loadJobs(1, true, mobileNumber);
        }
    };

    // Clear location dialog flag when user logs out (for testing)
    useEffect(() => {
        if (!isAuthenticated) {
            sessionStorage.removeItem(LOCATION_PREFERENCE_SHOWN_KEY);
        }
    }, [isAuthenticated]);


    /**
     * Initialize user data without loading jobs - prepare for location preference dialog
     */
    const initializeUser = async () => {
        // Get mobile number from localStorage or use '0' for non-authenticated users
        let userMobile = localStorage.getItem('userMobileNumber');
        
        if (!userMobile) {
            // For non-authenticated users, use mobile number "0"
            userMobile = '0';
            localStorage.setItem('userMobileNumber', userMobile);
        }
        
        console.log('[Homepage] Initializing user with mobile:', userMobile, 'Authentication status:', isAuthenticated);
        setMobileNumber(userMobile);
        
        // Set initial loading states but don't load jobs yet
        setLoading(true);
        setApiBusy(false); // Not busy yet since we're not calling API
        setError(null);
        console.log('[Homepage] initializeUser: preparing location data for preference dialog');
        
        // Get current location for the dialog
        let userLocation = null;
        try {
            console.log('[Homepage] Getting current location for preference dialog...');
            const forceFresh = !isAuthenticated; // Force fresh for non-authenticated users
            userLocation = await getCurrentLocation(forceFresh);
            
            // Get city name and pincode from coordinates using Google API
            let pincode = '';
            try {
                const locationDetails = await getLocationDetailsFromCoordinates(userLocation.latitude, userLocation.longitude);
                userLocation.city = locationDetails.cityName;
                pincode = locationDetails.pincode || '';
                console.log('[Homepage] Current location with city name and pincode:', userLocation, pincode);
            } catch (geocodingError) {
                console.warn('[Homepage] Reverse geocoding failed:', geocodingError);
                userLocation.city = `${userLocation.latitude.toFixed(4)}, ${userLocation.longitude.toFixed(4)}`;
            }
            
            setLocation(userLocation);
            console.log('[Homepage] Current location set for dialog:', userLocation);
            
            // Save candidate location if user is logged in
            if (isAuthenticated && userMobile && userMobile !== '0') {
                try {
                    console.log('[Homepage] Saving candidate location from GPS with pincode:', pincode);
                    locationSaveInProgressRef.current = true; // Mark save in progress
                    const saveResult = await saveCandidateLocation(
                        userLocation.latitude,
                        userLocation.longitude,
                        pincode
                    );
                    if (saveResult.success) {
                        console.log('[Homepage] Candidate GPS location saved successfully with pincode:', pincode);
                    } else {
                        console.warn('[Homepage] Failed to save candidate location:', saveResult.error);
                    }
                    // Keep flag true for 5 seconds to prevent duplicate saves from useEffect
                    setTimeout(() => { locationSaveInProgressRef.current = false; }, 5000);
                } catch (error) {
                    console.warn('[Homepage] Error saving candidate location:', error);
                    locationSaveInProgressRef.current = false;
                }
            }
        } catch (locationError) {
            console.warn('[Homepage] Failed to get current location:', locationError);
            // Use default location
            const defaultLocation = getDefaultLocation();
            try {
                const cityName = await getCityNameFromCoordinates(defaultLocation.latitude, defaultLocation.longitude);
                defaultLocation.city = cityName;
            } catch (geocodingError) {
                console.warn('[Homepage] Failed to get city name for default location, using generic fallback:', geocodingError);
                defaultLocation.city = 'your location';
            }
            setLocation(defaultLocation);
            userLocation = defaultLocation;
            console.log('[Homepage] Using default location for dialog:', defaultLocation);
            return defaultLocation; // Return the default location
        }
        
        // Get saved location for authenticated users
        if (isAuthenticated && userMobile && userMobile !== '0') {
            try {
                console.log('[Homepage] Fetching saved location for authenticated user...');
                const candidateResult = await getCandidateByMobile();
                if (candidateResult.success && candidateResult.data) {
                    const candidateData = candidateResult.data;
                    const candidateSavedLocation = candidateData.currentCity;
                    
                    if (candidateSavedLocation && 
                        candidateSavedLocation.latitude && 
                        candidateSavedLocation.longitude &&
                        candidateSavedLocation.cityName) {
                        
                        setSavedLocation(candidateSavedLocation);
                        console.log('[Homepage] Saved location set for dialog:', candidateSavedLocation);
                    } else {
                        console.log('[Homepage] No valid saved location found for user');
                    }
                }
            } catch (error) {
                console.warn('[Homepage] Error fetching saved location:', error);
            }
        }
        
        console.log('[Homepage] User initialization complete - waiting for location preference selection');
        return userLocation;
    };

    /**
     * Load jobs based on user's location
     */
    const loadJobs = useCallback(async (page = 1, reset = true, userMobile = null) => {
        try {
            // Do not start another fetch while one is in-flight
            if (isLoadingJobsRef.current) {
                console.log('[Homepage] loadJobs ignored: request already in-flight');
                return;
            }
            // Avoid parallelism with search-based filtering
            if (isSearchingRef.current) {
                console.log('[Homepage] loadJobs skipped: handleSearch in-flight');
                return;
            }
            isLoadingJobsRef.current = true;
            setApiBusy(true);
            console.log('[Homepage] API started, apiBusy set to true');
            if (reset) {
                setLoading(true);
                setLoadingMore(false);
                setError(null);
                // Clear current list so shimmer skeletons appear instead of stale cards
                setJobs([]);
                setAllJobs([]);
                setInitialFetchDone(false);
            } else {
                // This is a "Load More" request
                setLoadingMore(true);
            }

            // Use provided mobile number or state mobile number
            const mobile = userMobile || mobileNumber;
            
            let userLocation = location;
            let candidateSavedLocation = null;
            
            // Get user location if not already available
            if (!userLocation) {
                console.log('[Homepage] No cached location found. Attempting to get user location...');
                console.log('[Homepage] Authentication status:', isAuthenticated);
                console.log('[Homepage] Mobile number:', mobile);
                
                try {
                    // Always try to get current location for all users (authenticated and non-authenticated)
                    console.log('[Homepage] Requesting browser geolocation...');
                    // For non-authenticated users, force fresh location request to trigger permission dialog
                    const forceFresh = !isAuthenticated;
                    console.log('[Homepage] Force fresh location request:', forceFresh);
                    userLocation = await getCurrentLocation(forceFresh);
                    
                    console.log('[Homepage] Geolocation successful:', {
                        latitude: userLocation.latitude,
                        longitude: userLocation.longitude,
                        accuracy: userLocation.accuracy
                    });
                    
                    // Get actual city name and pincode from coordinates using Google API
                    let pincode = '';
                    try {
                        const locationDetails = await getLocationDetailsFromCoordinates(userLocation.latitude, userLocation.longitude);
                        userLocation.city = locationDetails.cityName;
                        pincode = locationDetails.pincode || '';
                        console.log('[Homepage] Reverse geocoding successful. City:', locationDetails.cityName, 'Pincode:', pincode);
                        
                        // Clear any previous error messages on successful location access
                        setError(null);
                        
                        // Show success message for non-authenticated users
                        if (!isAuthenticated) {
                            console.log('[Homepage] Location access granted for non-authenticated user');
                        }
                    } catch (geocodingError) {
                        console.warn('[Homepage] Reverse geocoding failed:', geocodingError);
                        userLocation.city = `${userLocation.latitude.toFixed(4)}, ${userLocation.longitude.toFixed(4)}`;
                    }
                    
                    setLocation(userLocation);
                    console.log('[Homepage] User location set successfully:', userLocation);
                    
                    // Save candidate location if user is logged in
                    if (isAuthenticated && mobile && mobile !== '0') {
                        try {
                            console.log('[Homepage] Saving candidate GPS location with pincode:', pincode);
                            const saveResult = await saveCandidateLocation(
                                userLocation.latitude,
                                userLocation.longitude,
                                pincode
                            );
                            if (saveResult.success) {
                                console.log('[Homepage] Candidate GPS location saved successfully with pincode:', pincode);
                            } else {
                                console.warn('[Homepage] Failed to save candidate location:', saveResult.error);
                            }
                        } catch (error) {
                            console.warn('[Homepage] Error saving candidate location:', error);
                        }
                    }
                } catch (locationError) {
                    console.warn('Failed to get user location:', locationError.message, locationError.type);

                    let fallbackResolved = false;

                    try {
                        console.log('[Homepage] Attempting IP-based location fallback');
                        const ipLocation = await getLocationFromIP();

                        if (ipLocation && Number.isFinite(ipLocation.latitude) && Number.isFinite(ipLocation.longitude)) {
                            userLocation = {
                                latitude: Number(ipLocation.latitude),
                                longitude: Number(ipLocation.longitude),
                                accuracy: ipLocation.accuracy ?? null,
                                source: ipLocation.source || 'ip',
                                city: ipLocation.city || ''
                            };

                            if (!userLocation.city || String(userLocation.city).trim() === '') {
                                try {
                                    userLocation.city = await getCityNameFromCoordinates(userLocation.latitude, userLocation.longitude);
                                } catch (ipReverseError) {
                                    console.warn('[Homepage] Reverse geocoding failed for IP fallback:', ipReverseError);
                                    userLocation.city = 'your location';
                                }
                            }

                            setLocation(userLocation);
                            const fallbackCityLabel = userLocation.city || 'your location';
                            const message = !isAuthenticated
                                ? `We couldn't access precise GPS data. Currently showing jobs near ${fallbackCityLabel}.`
                                : `Location permission declined. Showing jobs near ${fallbackCityLabel}.`;
                            setError(message);
                            fallbackResolved = true;
                        }
                    } catch (ipError) {
                        console.warn('[Homepage] IP-based location fallback failed:', ipError);
                    }

                    if (!fallbackResolved) {
                        userLocation = getDefaultLocation();
                        userLocation.city = 'your location';
                        setLocation(userLocation);

                        const message = !isAuthenticated
                            ? 'Please enable location access in your browser to see jobs near you. Currently showing jobs in your area.'
                            : 'Location access denied. Showing jobs in your area.';
                        setError(message);
                    }
                }
                
                // Location preference dialog will be handled by checkLocationPreference function
                // which properly calculates distance before showing dialog
            }
            
            // Check location preference after we have current location
            if (userLocation && mobile && mobile !== '0') {
                candidateSavedLocation = await checkLocationPreference(userLocation);
            }
            
            // If we already have savedLocation in state, use it
            if (!candidateSavedLocation && savedLocation) {
                candidateSavedLocation = savedLocation;
            }
            
            // If preference is saved but we don't have saved location yet, fetch it
            const currentPreference = sessionStorage.getItem(LOCATION_PREFERENCE_KEY);
            if (currentPreference === 'saved' && !candidateSavedLocation && mobile && mobile !== '0') {
                console.log('[Homepage] Preference is saved but no saved location, fetching...');
                try {
                    const candidateResult = await getCandidateByMobile();
                    if (candidateResult.success && candidateResult.data) {
                        candidateSavedLocation = candidateResult.data.currentCity;
                        if (candidateSavedLocation) {
                            setSavedLocation(candidateSavedLocation);
                        }
                    }
                } catch (error) {
                    console.warn('[Homepage] Error fetching saved location:', error);
                }
            }
            
            // Determine which location to use based on preference
            let jobLocation = userLocation;
            const preference = sessionStorage.getItem(LOCATION_PREFERENCE_KEY);
            console.log('[Homepage] Current preference from session:', preference);
            console.log('[Homepage] Candidate saved location available:', candidateSavedLocation);
            console.log('[Homepage] Saved location in state:', savedLocation);
            
            if (preference === 'saved' && candidateSavedLocation) {
                // Use saved location from candidate data
                jobLocation = {
                    latitude: Number(candidateSavedLocation.latitude),
                    longitude: Number(candidateSavedLocation.longitude),
                    city: candidateSavedLocation.cityName
                };
                console.log('[Homepage] Using saved location for jobs:', {
                    city: jobLocation.city,
                    latitude: jobLocation.latitude,
                    longitude: jobLocation.longitude,
                    originalLat: candidateSavedLocation.latitude,
                    originalLng: candidateSavedLocation.longitude
                });
            } else if (preference === 'saved' && !candidateSavedLocation) {
                console.log('[Homepage] Preference is saved but no saved location available yet');
            } else {
                console.log('[Homepage] Using current location for jobs:', {
                    city: jobLocation.city || 'Unknown',
                    latitude: jobLocation.latitude,
                    longitude: jobLocation.longitude
                });
            }
            
            if (!mobile) {
                throw new Error('Mobile number is required to fetch jobs');
            }

            // Guard: only call network once per page load; hydrate from cache if duplicate within TTL
            const jobsFetchKey = 'homepage_jobs_fetched_at';
            const jobsCacheKey = 'homepage_jobs_cache';
            const nowTs = Date.now();
            let lastJobsTs = 0;
            try { lastJobsTs = parseInt(sessionStorage.getItem(jobsFetchKey) || '0', 10) || 0; } catch (_) {}
            const JOBS_TTL_MS = 10000; // 10s window
            if (lastJobsTs && (nowTs - lastJobsTs) <= JOBS_TTL_MS) {
                // Try hydrate from cache to avoid empty UI under StrictMode remount
                try {
                    const cachedStr = sessionStorage.getItem(jobsCacheKey);
                    if (cachedStr) {
                        const cached = JSON.parse(cachedStr);
                        const finalJobs = Array.isArray(cached?.jobs) ? cached.jobs : [];
                        if (reset) {
                            setJobs(finalJobs);
                            setAllJobs(finalJobs);
                        } else {
                            setJobs(prev => [...prev, ...finalJobs]);
                            setAllJobs(prev => [...prev, ...finalJobs]);
                        }
                        setHasMoreJobs(finalJobs.length === 10);
                        setCurrentPage(page);
                        setError(null);
                        if (!initialFetchDone) setInitialFetchDone(true);
                        setLoading(false);
                        setLoadingMore(false);
                        isLoadingJobsRef.current = false;
                        setApiBusy(false);
                        setHasInitialLoadCompleted(true);
                        console.log('[Homepage] Hydrated jobs from cache due to TTL guard');
                        return;
                    }
                } catch (_) { /* ignore parse errors */ }
            }
            try { sessionStorage.setItem(jobsFetchKey, String(nowTs)); } catch (_) {}

            // Ensure coordinates are numbers
            const lat = Number(jobLocation.latitude);
            const lng = Number(jobLocation.longitude);
            
            console.log('[Homepage] Final coordinates for API call:', { lat, lng, city: jobLocation.city });
            console.log('[Homepage] Original jobLocation object:', jobLocation);
            console.log('[Homepage] Preference being used:', preference);
            
            // Update the actual job location state for the header display
            setActualJobLocation(jobLocation);
            
            // Fetch filter options with same coordinates as jobs API
            try {
                console.log('[Homepage] About to call fetchJobsFilterOptions with:', { lat, lng, radius: 150 });
                const filterData = await fetchJobsFilterOptions(lat, lng, 150); // 150km radius
                if (filterData) {
                    setFilterOptions({
                        designations: filterData.designations || [],
                        locations: filterData.locations || [],
                        skills: filterData.skills || []
                    });
                    console.log('[Homepage] Filter options loaded with jobs API coordinates:', filterData);
                }
            } catch (e) {
                console.warn('[Homepage] Filter options fetch failed:', e);
            }

            // Use radius-based fetching for all users
            if (reset) {
                console.log('[Homepage] 🔄 Resetting radius state for fresh search');
                resetRadius();
                // Clear active filters for normal (non-filtered) search
                setActiveFilters({ skillIds: [], designationIds: [], keywords: [] });
            }
            
            console.log('[Homepage] 🎯 Using radius-based job fetching');
            await fetchJobsWithRadius(lat, lng, jobLocation.city);
        } catch (err) {
            console.error('Error loading jobs:', err);
            setError(err.message || 'Failed to load jobs. Please try again.');
            // Mark initial fetch as done even on error so empty state can show
            if (!initialFetchDone) {
                setInitialFetchDone(true);
                console.log('[Homepage] Initial fetch completed with error');
            }
        } finally {
            setLoading(false);
            setLoadingMore(false);
            isLoadingJobsRef.current = false;
            setApiBusy(false);
            console.log('[Homepage] API finished, apiBusy set to false');
            setHasInitialLoadCompleted(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, selectedLanguage, mobileNumber, initialFetchDone, savedLocation, checkLocationPreference, isAuthenticated]);

    // For non-authenticated users, explicitly request location permission on mount
    useEffect(() => {
        if (!isAuthenticated) {
            console.log('[Homepage] Non-authenticated user detected, clearing any cached location and requesting fresh permission');

            // Clear any cached location for non-authenticated users to force fresh permission request
            setLocation(null);

            let cancelled = false;

            const requestFreshLocation = async () => {
                try {
                    console.log('[Homepage] Triggering fresh location request for non-authenticated user');
                    console.log('[Homepage] This should show browser\'s native location permission dialog');

                    // Force a fresh location request with maximumAge: 0 (this should trigger the browser dialog)
                    const userLocation = await getCurrentLocation(true); // forceFresh = true
                    console.log('[Homepage] Location permission granted for non-authenticated user:', userLocation);

                    try {
                        const cityName = await getCityNameFromCoordinates(userLocation.latitude, userLocation.longitude);
                        userLocation.city = cityName;
                        console.log('[Homepage] City name resolved:', cityName);
                    } catch (geocodingError) {
                        console.warn('[Homepage] Reverse geocoding failed:', geocodingError);
                        userLocation.city = `${userLocation.latitude.toFixed(4)}, ${userLocation.longitude.toFixed(4)}`;
                    }

                    if (cancelled) {
                        return;
                    }

                    setLocation(userLocation);
                    setError(null); // Clear any previous errors
                    console.log('[Homepage] Location successfully set for non-authenticated user:', userLocation);
                } catch (locationError) {
                    console.warn('[Homepage] Location permission denied or failed for non-authenticated user:', locationError);
                    console.log('[Homepage] Error type:', locationError.type);
                    console.log('[Homepage] Error message:', locationError.message);

                    const defaultLocation = getDefaultLocation();
                    try {
                        const cityName = await getCityNameFromCoordinates(defaultLocation.latitude, defaultLocation.longitude);
                        defaultLocation.city = cityName;
                        console.log('[Homepage] Using default location with city:', cityName);
                    } catch (fallbackError) {
                        console.warn('[Homepage] Default location geocoding failed:', fallbackError);
                        defaultLocation.city = 'your location';
                    }

                    if (cancelled) {
                        return;
                    }

                    setLocation(defaultLocation);
                    setError(`Please enable location access in your browser to see jobs near you. Currently showing jobs near ${defaultLocation.city}.`);
                }
            };

            requestFreshLocation();

            return () => {
                cancelled = true;
            };
        }
    }, [isAuthenticated]); // Only depend on isAuthenticated, not location

    // Load jobs when component mounts (guarded against React StrictMode double-invoke)
    const didInitRef = useRef(false);
    useEffect(() => {
        if (didInitRef.current) return;
        didInitRef.current = true;
        
        const initializeApp = async () => {
            console.log('[Homepage] Starting app initialization');
            
            // Initialize user data first to get mobile number and current location
            const currentUserLocation = await initializeUser();
            
            // Check if user is authenticated by looking at mobile number and authentication state
            const userMobile = mobileNumber || localStorage.getItem('userMobileNumber');
            const isUserAuthenticated = isAuthenticated || (userMobile && userMobile !== '0');
            
            console.log('[Homepage] Authentication check:', {
                isAuthenticated,
                userMobile,
                isUserAuthenticated,
                currentUserLocation
            });
            
            // For authenticated users, check if they need location preference dialog
            if (isUserAuthenticated && userMobile && userMobile !== '0') {
                console.log('[Homepage] Authenticated user detected - checking location preference');
                
                // Check if preference was already shown this session
                const hasShownPreference = sessionStorage.getItem(LOCATION_PREFERENCE_SHOWN_KEY);
                
                if (!hasShownPreference) {
                    try {
                        // Fetch candidate details to get base location
                        console.log('[Homepage] Fetching candidate details for base location...');
                        const candidateResult = await getCandidateByMobile();
                        
                        if (candidateResult.success && candidateResult.data) {
                            const candidateData = candidateResult.data;
                            const baseLocation = candidateData.currentCity || candidateData.preferredCity;
                            
                            if (baseLocation && baseLocation.latitude && baseLocation.longitude) {
                                setSavedLocation(baseLocation);
                                console.log('[Homepage] Base location found:', baseLocation);
                                
                                // Use current location returned from initializeUser
                                console.log('[Homepage] Current user location:', currentUserLocation);
                                
                                if (currentUserLocation && currentUserLocation.latitude && currentUserLocation.longitude) {
                                    // Check if user is within 25km of base location
                                    const withinRadius = isWithinRadius(currentUserLocation, baseLocation, 25);
                                    console.log('[Homepage] Within 25km radius?', withinRadius);
                                    
                                    if (!withinRadius) {
                                        // User is outside 25km, show preference dialog
                                        console.log('[Homepage] User outside 25km radius - showing preference dialog');
                                        setShowLocationPreferenceDialog(true);
                                        return; // Wait for user selection
                                    } else {
                                        // User is within 25km, use current location without showing dialog
                                        console.log('[Homepage] User within 25km radius - using current location automatically');
                                        sessionStorage.setItem(LOCATION_PREFERENCE_SHOWN_KEY, 'true');
                                        sessionStorage.setItem(LOCATION_PREFERENCE_KEY, 'current');
                                        loadJobs(1, true, userMobile);
                                    }
                                } else {
                                    // Could not get current location, show preference dialog
                                    console.log('[Homepage] Could not get current location - showing preference dialog');
                                    setShowLocationPreferenceDialog(true);
                                }
                            } else {
                                // No valid base location, proceed without preference dialog
                                console.log('[Homepage] No valid base location found - loading jobs directly');
                                loadJobs(1, true, userMobile);
                            }
                        } else {
                            // Could not fetch candidate details, proceed without preference dialog
                            console.log('[Homepage] Could not fetch candidate details - loading jobs directly');
                            loadJobs(1, true, userMobile);
                        }
                    } catch (error) {
                        console.error('[Homepage] Error in location preference check:', error);
                        // On error, proceed without preference dialog
                        loadJobs(1, true, userMobile);
                    }
                } else {
                    // Preference already shown this session, load jobs directly
                    console.log('[Homepage] Preference already shown this session - loading jobs directly');
                    loadJobs(1, true, userMobile);
                }
            } else {
                console.log('[Homepage] Non-authenticated user - loading jobs directly without location preference dialog');
                // Load jobs immediately for non-authenticated users without showing location preference dialog
                console.log('[Homepage] Loading jobs directly for non-authenticated user');
                loadJobs(1, true, '0'); // Use mobile '0' for non-authenticated users
            }
        };
        
        // App-level initProgrammaticToken runs on app load; proceed to initialize user
        initializeApp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Remove isAuthenticated dependency to avoid re-running when auth state changes

    // Save location when user becomes authenticated
    useEffect(() => {
        const saveLocationForAuthenticatedUser = async () => {
            // Only run if user is authenticated and has a mobile number
            if (!isAuthenticated || !mobileNumber || mobileNumber === '0') {
                return;
            }

            // Only run if we have a location
            if (!location || !location.latitude || !location.longitude) {
                return;
            }

            // Prevent duplicate saves - if already saving, skip
            if (locationSaveInProgressRef.current) {
                console.log('[Homepage] Location save already in progress, skipping duplicate save');
                return;
            }

            console.log('[Homepage] User authenticated, saving current location...');
            locationSaveInProgressRef.current = true;

            try {
                // Get pincode from coordinates using OpenStreetMap API
                let pincode = '';
                try {
                    console.log('[Homepage] Fetching pincode for coordinates:', location.latitude, location.longitude);
                    const locationDetails = await getLocationDetailsFromCoordinates(location.latitude, location.longitude);
                    pincode = locationDetails.pincode || '';
                    console.log('[Homepage] Got pincode for authenticated user location:', pincode);
                } catch (geocodingError) {
                    console.warn('[Homepage] Failed to get pincode for authenticated user:', geocodingError);
                }

                // Save the location with pincode
                console.log('[Homepage] Calling saveCandidateLocation with pincode:', pincode);
                const saveResult = await saveCandidateLocation(
                    location.latitude,
                    location.longitude,
                    pincode
                );

                if (saveResult.success) {
                    console.log('[Homepage] Location saved successfully for authenticated user with pincode:', pincode);
                } else {
                    console.warn('[Homepage] Failed to save location for authenticated user:', saveResult.error);
                }
            } catch (error) {
                console.error('[Homepage] Error saving location for authenticated user:', error);
            } finally {
                // Reset the flag after a delay to allow for subsequent saves if location changes
                setTimeout(() => {
                    locationSaveInProgressRef.current = false;
                }, 5000);
            }
        };

        saveLocationForAuthenticatedUser();
    }, [isAuthenticated, mobileNumber, location]); // Run when authentication state, mobile number, or location changes

    // Listen for UrlRouter events to open Job Details modal
    useEffect(() => {
        // If UrlRouter dispatched before this mounted, read from sessionStorage
        try {
            const cached = sessionStorage.getItem('pendingJobDetails');
            if (cached) {
                const job = JSON.parse(cached);
                console.log('[Homepage] Found cached job details from sessionStorage:', job);
                if (job) {
                    // Process cached job data with same defaults as event handler
                    const jobWithDefaults = {
                        ...job,
                        title: job.title || job.jobName || job.budgetName || 'Job Details',
                        location: job.location || (job.cityName && job.pincode ? `${job.cityName} - ${job.pincode}` : 'Location Not Specified'),
                        salary: job.salary || (job.maxSalaryLimit ? `Up to ₹${job.maxSalaryLimit.toLocaleString()} per month` : 'Salary Not Disclosed')
                    };
                    
                    console.log('[Homepage] Opening JobDetailsModal with cached job data:', jobWithDefaults);
                    setSelectedJobForDetails(jobWithDefaults);
                    setIsJobDetailsModalOpen(true);
                }
                sessionStorage.removeItem('pendingJobDetails');
            }
        } catch (error) {
            console.warn('[Homepage] Error processing cached job details:', error);
        }

        const onOpenJobDetails = (e) => {
            const job = e?.detail?.job;
            console.log('[Homepage] Received openJobDetails event with job:', job);
            if (job) {
                // Ensure we have the necessary job data before opening modal
                const jobWithDefaults = {
                    ...job,
                    title: job.title || job.jobName || job.budgetName || 'Job Details',
                    location: job.location || (job.cityName && job.pincode ? `${job.cityName} - ${job.pincode}` : 'Location Not Specified'),
                    salary: job.salary || (job.maxSalaryLimit ? `Up to ₹${job.maxSalaryLimit.toLocaleString()} per month` : 'Salary Not Disclosed')
                };
                
                console.log('[Homepage] Opening JobDetailsModal with processed job data:', jobWithDefaults);
                setSelectedJobForDetails(jobWithDefaults);
                setIsJobDetailsModalOpen(true);
            } else {
                console.warn('[Homepage] openJobDetails event received but no job data found');
            }
        };
        window.addEventListener('openJobDetails', onOpenJobDetails);
        return () => window.removeEventListener('openJobDetails', onOpenJobDetails);
    }, []);
    

    // Removed boot loader overlay; skeletons in FeaturedJobs handle loading UX

    // Reload jobs when language changes (skip the very first run after initial mobile set)
    useEffect(() => {
        const langCode = selectedLanguage?.code || 'en';
        if (!mobileNumber) return;
        if (skipNextLanguageReload.current) {
            // Skip the first effect run which is caused by initial mobile number set
            skipNextLanguageReload.current = false;
            return;
        }
        console.log('[Homepage] Language changed, reloading jobs with language:', langCode);
        loadJobs(1, true, mobileNumber);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLanguage?.code, mobileNumber]);

    // ========== RADIUS-BASED FETCHING FUNCTIONS ==========
    
    /**
     * Fetch jobs within current radius
     * @param {number} lat - Latitude
     * @param {number} lng - Longitude
     * @param {string} locationName - Location name
     * @param {number} radiusIndexOverride - Optional radius index to use (for expansion)
     * @param {boolean} isExpanding - Whether this is an expansion fetch (to avoid state race condition)
     * @param {number} radiusOverride - Optional radius value in km (to avoid state race condition)
     * @param {object} filters - Optional filters { skillIds: [], designationIds: [] }
     */
    const fetchJobsWithRadius = async (lat, lng, locationName, radiusIndexOverride = null, isExpanding = false, radiusOverride = null, filters = null) => {
        console.log('[Homepage] 🎯 fetchJobsWithRadius() called with:', {
            lat,
            lng,
            locationName,
            radiusIndexOverride,
            isExpanding,
            currentRadiusIndex,
            currentRadius,
            hasCompletedCurrentRadiusFetch,
            isExpandingRadius
        });

        // Use override if provided, otherwise use current state
        const effectiveRadiusIndex = radiusIndexOverride !== null ? radiusIndexOverride : currentRadiusIndex;
        const isExpansion = effectiveRadiusIndex > 0;
        
        console.log('[Homepage] 📊 Calculated values:', {
            effectiveRadiusIndex,
            isExpansion,
            isExpanding,
            willSkip: hasCompletedCurrentRadiusFetch && !isExpanding
        });
        
        // Prevent duplicate fetches for the same radius
        // Use isExpanding parameter instead of isExpandingRadius state to avoid race condition
        if (hasCompletedCurrentRadiusFetch && !isExpanding) {
            console.log('[Homepage] ⚠️ Already fetched jobs for', currentRadius, 'km radius, skipping duplicate fetch');
            return;
        }

        try {
            const mobile = mobileNumber || localStorage.getItem('userMobileNumber') || '0';
            
            // Use radius override if provided, otherwise use current state
            const effectiveRadius = radiusOverride !== null ? radiusOverride : currentRadius;
            
            // Use provided filters or active filters from state
            const effectiveFilters = filters || activeFilters;
            
            console.log('[Homepage] 🎯 Fetching jobs within', effectiveRadius, 'km radius (index:', effectiveRadiusIndex, ', isExpansion:', isExpansion, ')');
            console.log('[Homepage] 📍 Coordinates:', { lat, lng });
            console.log('[Homepage] 👤 Mobile:', mobile);
            console.log('[Homepage] 🔍 Filters:', effectiveFilters);

            const response = await searchJobs({
                latitude: lat,
                longitude: lng,
                distance: effectiveRadius,
                skillIds: effectiveFilters.skillIds || [],
                designationIds: effectiveFilters.designationIds || [],
                keywords: effectiveFilters.keywords || [],
                page: 1,
                size: 1000, // Fetch all jobs within radius
                mobileNumber: mobile,
            });

            if (response.success && response.data) {
                const fetchedJobs = response.data.jobs || response.data || [];
                console.log('[Homepage] 🔍 Fetched', fetchedJobs.length, 'jobs from API');
                console.log('[Homepage] 🔍 Currently tracking', fetchedJobIds.size, 'unique job IDs');

                // Filter out duplicates (jobs already fetched in previous radius)
                console.log('[Homepage] 🔍 Filtering duplicates from', fetchedJobs.length, 'fetched jobs');
                console.log('[Homepage] 🔍 Tracked job IDs:', Array.from(fetchedJobIds).slice(0, 10), '... (showing first 10)');
                console.log('[Homepage] 🔍 Sample job from API:', fetchedJobs[0]);
                
                const newJobs = fetchedJobs.filter(job => {
                    // Use positionId as the primary unique identifier (most stable field)
                    const jobId = job.positionId || job.budgetCityId || job.id || job.jobId;
                    const isDuplicate = fetchedJobIds.has(jobId);
                    if (isDuplicate) {
                        console.log('[Homepage] ⚠️ Skipping duplicate job:', jobId, '-', job.jobName || job.title);
                    } else {
                        console.log('[Homepage] ✅ New job:', jobId, '-', job.jobName || job.title);
                    }
                    return !isDuplicate;
                });

                console.log('[Homepage] ✅ After duplicate filter:', newJobs.length, 'new jobs out of', fetchedJobs.length, 'fetched');
                console.log('[Homepage] ✅ Duplicate jobs filtered out:', fetchedJobs.length - newJobs.length);

                // Transform jobs
                const transformedJobs = newJobs.map(job => {
                    let skills = job.skills || job.tags || job.requirements || 'General';
                    if (!skills || (typeof skills !== 'string' && !Array.isArray(skills))) {
                        skills = 'General';
                    }
                    // Use positionId as primary ID (most stable field)
                    const stableId = job.positionId || job.budgetCityId || job.id || job.jobId || Math.random().toString(36);
                    return {
                        ...job,
                        id: stableId,
                        skills,
                        title: job.jobName || job.title || 'Job Title Not Available',
                        location: job.cityName && job.pincode ? `${job.cityName} - ${job.pincode}` : 'Location Not Specified',
                        salary: job.maxSalaryLimit ? `Up to ₹${job.maxSalaryLimit.toLocaleString()} per month` : 'Salary Not Disclosed'
                    };
                });

                // Translate if needed
                const currentLanguage = selectedLanguage?.code || 'en';
                let finalJobs = transformedJobs;
                if (TranslationUtils.isTranslationNeeded(currentLanguage)) {
                    console.log('[Homepage] Translating jobs to language:', currentLanguage);
                    finalJobs = await TranslationUtils.translateJobFields(
                        transformedJobs,
                        currentLanguage,
                        ['jobName', 'jobDetails', 'cityName']
                    );
                }

                // Add new job IDs to tracking set
                const newFetchedJobIds = new Set(fetchedJobIds);
                finalJobs.forEach(job => {
                    newFetchedJobIds.add(job.id);
                });
                setFetchedJobIds(newFetchedJobIds);

                // Update jobs buffer and display first batch
                if (!isExpansion) {
                    // First fetch - replace buffer and jobs
                    setJobsBuffer(finalJobs);
                    setAllJobs(finalJobs);
                    setDisplayedJobCount(0);

                    // Display first batch (up to 10 jobs)
                    const initialBatchSize = finalJobs.length < batchSize ? finalJobs.length : batchSize;
                    setJobs(finalJobs.slice(0, initialBatchSize));
                    setDisplayedJobCount(initialBatchSize);

                    console.log('[Homepage] 📋 Initial fetch: Buffered', finalJobs.length, 'jobs, displaying first', initialBatchSize, 'jobs');
                } else {
                    // Expansion - append new jobs to buffer
                    setJobsBuffer(prev => {
                        // Additional safety check: filter out any jobs already in buffer
                        const existingIds = new Set(prev.map(j => j.id));
                        const trulyNewJobs = finalJobs.filter(j => !existingIds.has(j.id));
                        if (trulyNewJobs.length < finalJobs.length) {
                            console.warn('[Homepage] ⚠️ Filtered out', finalJobs.length - trulyNewJobs.length, 'duplicate jobs from buffer append');
                        }
                        return [...prev, ...trulyNewJobs];
                    });
                    
                    setAllJobs(prev => {
                        const existingIds = new Set(prev.map(j => j.id));
                        const trulyNewJobs = finalJobs.filter(j => !existingIds.has(j.id));
                        return [...prev, ...trulyNewJobs];
                    });

                    // Display first batch of new jobs (with additional duplicate check)
                    setJobs(prev => {
                        const existingIds = new Set(prev.map(j => j.id));
                        const trulyNewJobs = finalJobs.filter(j => !existingIds.has(j.id));
                        const newJobsToDisplay = trulyNewJobs.length < batchSize ? trulyNewJobs.length : batchSize;
                        const jobsToAdd = trulyNewJobs.slice(0, newJobsToDisplay);
                        
                        console.log('[Homepage] 📋 Expansion: Adding', jobsToAdd.length, 'new jobs to display (filtered from', finalJobs.length, ')');
                        return [...prev, ...jobsToAdd];
                    });
                    
                    setDisplayedJobCount(prev => {
                        const existingIds = new Set(jobs.map(j => j.id));
                        const trulyNewJobs = finalJobs.filter(j => !existingIds.has(j.id));
                        const newJobsToDisplay = trulyNewJobs.length < batchSize ? trulyNewJobs.length : batchSize;
                        return prev + newJobsToDisplay;
                    });

                    console.log('[Homepage] 📋 Expansion: Buffered', finalJobs.length, 'new jobs');
                }

                console.log('[Homepage] 📋 Total unique job IDs tracked:', newFetchedJobIds.size);

                // Mark current radius fetch as complete
                setHasCompletedCurrentRadiusFetch(true);
                console.log('[Homepage] ✅ Completed fetch for', currentRadius, 'km radius');

                // Check if we reached max radius
                if (currentRadiusIndex >= radiusLevels.length - 1) {
                    setHasMoreRadiusLevels(false);
                    console.log('[Homepage] 🏁 Reached maximum radius (', currentRadius, 'km)');
                }

                setError(null);
                
                // Return number of new jobs found (for auto-expansion logic)
                return finalJobs.length;
            } else {
                console.log('[Homepage] API Error:', response);
                throw new Error(response.error || 'Failed to fetch jobs');
            }
        } catch (err) {
            console.error('[Homepage] Error fetching jobs with radius:', err);
            setError(err.message || 'Failed to load jobs. Please try again.');
            // Still mark as complete to prevent retry loops
            setHasCompletedCurrentRadiusFetch(true);
            return 0; // Return 0 new jobs on error
        } finally {
            // Only set loading to false if this is not an expansion
            // (expansion manages its own loading state)
            if (!isExpansion) {
                setLoading(false);
            }
            setLoadingMore(false);
            isLoadingJobsRef.current = false;
            setApiBusy(false);
            setHasInitialLoadCompleted(true);
            if (!initialFetchDone) {
                setInitialFetchDone(true);
            }
        }
    };

    /**
     * Load next batch of jobs from buffer (for lazy loading)
     */
    const loadNextBatchFromBuffer = async () => {
        // Don't load if already loading or no more jobs in buffer
        if (isLoadingNextBatch || !hasMoreJobsInBuffer) {
            console.log('[Homepage] ⚠️ Cannot load next batch: isLoading=', isLoadingNextBatch, ', hasMore=', hasMoreJobsInBuffer);
            return;
        }

        try {
            setIsLoadingNextBatch(true);
            setLoadingMore(true);

            // Simulate slight delay for smooth UX
            await new Promise(resolve => setTimeout(resolve, 300));

            // Calculate how many jobs to display next
            const remainingJobs = jobsBuffer.length - displayedJobCount;
            const jobsToAdd = remainingJobs < batchSize ? remainingJobs : batchSize;

            // Get next batch from buffer
            const nextBatch = jobsBuffer.slice(displayedJobCount, displayedJobCount + jobsToAdd);

            // Add to displayed jobs
            setJobs(prev => [...prev, ...nextBatch]);
            setDisplayedJobCount(prev => prev + jobsToAdd);

            console.log('[Homepage] 📦 Loaded next batch:', jobsToAdd, 'jobs');
            console.log('[Homepage] 📊 Progress:', displayedJobCount + jobsToAdd, '/', jobsBuffer.length, 'jobs displayed');

        } catch (e) {
            console.error('[Homepage] Error loading next batch:', e);
        } finally {
            setIsLoadingNextBatch(false);
            setLoadingMore(false);
        }
    };

    /**
     * Expand search radius to next level (with user consent)
     */
    const expandRadius = async () => {
        console.log('[Homepage] 🚀 expandRadius() called');
        console.log('[Homepage] 📊 State check:', {
            canExpandRadius,
            currentRadiusIndex,
            currentRadius,
            hasMoreRadiusLevels,
            isExpandingRadius,
            location: location || actualJobLocation
        });

        if (!canExpandRadius) {
            setHasMoreRadiusLevels(false);
            console.log('[Homepage] 🛑 Cannot expand radius - already at maximum');
            return;
        }

        try {
            console.log('[Homepage] ⚙️ Setting isExpandingRadius to true');
            setIsExpandingRadius(true);
            setHasCompletedCurrentRadiusFetch(false); // Reset flag for new radius

            // Move to next radius level
            const newRadiusIndex = currentRadiusIndex + 1;
            const newRadius = radiusLevels[newRadiusIndex];
            
            console.log('[Homepage] 📡 Expanding radius from', currentRadius, 'km to', newRadius, 'km');
            console.log('[Homepage] 📊 Current state before expansion:', {
                currentRadiusIndex,
                newRadiusIndex,
                displayedJobCount,
                bufferSize: jobsBuffer.length,
                fetchedJobIdsCount: fetchedJobIds.size,
                jobsLength: jobs.length
            });

            // Update radius state
            console.log('[Homepage] ⚙️ Updating radius state...');
            setCurrentRadiusIndex(newRadiusIndex);
            setCurrentRadius(newRadius);

            // Get coordinates - prioritize actualJobLocation (updated by searches) over location (device location)
            const lat = actualJobLocation?.latitude || location?.latitude;
            const lng = actualJobLocation?.longitude || location?.longitude;
            const city = actualJobLocation?.city || location?.city;

            console.log('[Homepage] 📍 Coordinates for expansion:', { lat, lng, city });
            console.log('[Homepage] 📍 Using actualJobLocation:', actualJobLocation);
            console.log('[Homepage] 📍 Device location:', location);

            if (!lat || !lng) {
                throw new Error('Location coordinates not available');
            }

            // Fetch jobs with new radius - pass newRadiusIndex, isExpanding=true, and newRadius to avoid race conditions
            console.log('[Homepage] 🔄 Calling fetchJobsWithRadius with radiusIndex:', newRadiusIndex, 'and radius:', newRadius, 'km');
            let newJobsCount = await fetchJobsWithRadius(lat, lng, city, newRadiusIndex, true, newRadius);

            console.log('[Homepage] ✅ Radius expansion complete to', newRadius, 'km, found', newJobsCount, 'new jobs');
            
            // Auto-expand if we got less than 10 new jobs and more radius levels available
            const minJobsThreshold = 10;
            let currentIndex = newRadiusIndex;
            let totalNewJobs = newJobsCount;
            
            // Keep expanding until we have enough jobs or reach max radius
            while (totalNewJobs < minJobsThreshold && currentIndex < radiusLevels.length - 1) {
                console.log('[Homepage] 🔄 Auto-expanding: Only', totalNewJobs, 'new jobs found (< ', minJobsThreshold, '), expanding to next radius...');
                
                // Move to next radius
                currentIndex = currentIndex + 1;
                const autoExpandRadius = radiusLevels[currentIndex];
                
                console.log('[Homepage] 🔄 Auto-expanding to', autoExpandRadius, 'km (index:', currentIndex, ')');
                setCurrentRadiusIndex(currentIndex);
                setCurrentRadius(autoExpandRadius);
                setHasCompletedCurrentRadiusFetch(false);
                
                // Fetch jobs with next radius
                const additionalJobsCount = await fetchJobsWithRadius(lat, lng, city, currentIndex, true, autoExpandRadius);
                totalNewJobs += additionalJobsCount;
                
                console.log('[Homepage] ✅ Auto-expansion to', autoExpandRadius, 'km complete, found', additionalJobsCount, 'additional jobs');
                console.log('[Homepage] 📊 Total new jobs accumulated:', totalNewJobs);
            }
            
            if (totalNewJobs >= minJobsThreshold) {
                console.log('[Homepage] ✅ Auto-expansion stopped: Accumulated', totalNewJobs, 'jobs (>= ', minJobsThreshold, ')');
            } else {
                console.log('[Homepage] 🏁 Auto-expansion stopped: Reached max radius');
            }
            
            // Check if we've reached max radius after auto-expansion
            if (currentIndex >= radiusLevels.length - 1) {
                setHasMoreRadiusLevels(false);
                console.log('[Homepage] 🏁 Setting hasMoreRadiusLevels to false (max radius reached)');
            }

        } catch (e) {
            console.error('[Homepage] ❌ Error expanding radius:', e);
            console.error('[Homepage] ❌ Error stack:', e.stack);
            setError('Failed to expand search radius. Please try again.');
        } finally {
            console.log('[Homepage] ⚙️ Setting isExpandingRadius to false');
            setIsExpandingRadius(false);
        }
    };

    /**
     * Reset radius state (when location changes or user starts new search)
     */
    const resetRadius = () => {
        console.log('[Homepage] 🔄 Resetting radius state');
        setCurrentRadiusIndex(0);
        setCurrentRadius(50);
        setFetchedJobIds(new Set());
        setHasMoreRadiusLevels(true);
        setIsExpandingRadius(false);
        setHasCompletedCurrentRadiusFetch(false);

        // Reset lazy loading state
        setJobsBuffer([]);
        setDisplayedJobCount(0);
        setIsLoadingNextBatch(false);
    };

    /**
     * Decline radius expansion (user doesn't want to see more jobs)
     */
    const declineRadiusExpansion = () => {
        console.log('[Homepage] ❌ User declined radius expansion');
        setHasMoreRadiusLevels(false);
    };

    // ========== END RADIUS-BASED FETCHING FUNCTIONS ==========

    /**
     * Load more jobs - Updated to handle radius-based mode
     */
    const loadMoreJobs = async () => {
        if (loading || !mobileNumber) return;
        
        // For radius-based mode, load next batch from buffer
        if (isRadiusBasedMode && !isServerFiltered) {
            if (hasMoreJobsInBuffer) {
                console.log('[Homepage] 📦 Loading next batch from buffer...');
                await loadNextBatchFromBuffer();
            } else {
                console.log('[Homepage] 📍 All buffered jobs displayed, ready for radius expansion');
            }
            return;
        }
        
        // For server-filtered mode with radius-based fetching, use buffer
        // (We already fetched all filtered jobs with size: 1000, just load next batch from buffer)
        if (isServerFiltered) {
            console.log('[Homepage] 📦 Loading next batch of filtered jobs from buffer...');
            if (hasMoreJobsInBuffer) {
                await loadNextBatchFromBuffer();
            }
            return;
        }
        
        // Old pagination code (kept for backward compatibility if needed)
        if (!hasMoreJobs) return;
        if (false) { // Disabled - using buffer-based loading now
            // Prevent overlap with other requests
            if (isLoadingJobsRef.current || isSearchingRef.current) return;
            try {
                isLoadingJobsRef.current = true;
                setLoadingMore(true);
                setApiBusy(true);

                // Build payload from current filter chips - single location only
                const locationPayload = selectedCityCoord ? {
                    lat: Number(selectedCityCoord.latitude),
                    lng: Number(selectedCityCoord.longitude),
                    radius: 150,
                } : null;
                // Skills payload: only from selectedSkills (skills remain strings)
                const skillsPayload = (selectedSkills || []).map(s => String(s).trim()).filter(Boolean);

                // Designations: map selected designation labels to IDs
                const designationMap = new Map(
                    (Array.isArray(filterOptions?.designations) ? filterOptions.designations : [])
                        .map(d => [String(d.label || d.name || d.title || '').trim().toLowerCase(), Number(d.id)])
                );
                const designationIdsPayload = (selectedDesignations || [])
                    .map(lbl => designationMap.get(String(lbl).toLowerCase()))
                    .filter(id => Number.isInteger(id));
                const unmappedDesignations = (selectedDesignations || []).filter(lbl => !designationMap.has(String(lbl).toLowerCase()));
                if (unmappedDesignations.length > 0) {
                    console.warn('[Homepage] (pagination) Unmapped designation labels (no corresponding ID):', unmappedDesignations);
                }

                const nextPage = currentPage + 1;
                
                const response = await searchJobs({
                    latitude: locationPayload?.lat || location?.latitude || 28.6139,
                    longitude: locationPayload?.lng || location?.longitude || 77.2090,
                    distance: locationPayload?.radius || 150,
                    skillIds: skillsPayload,
                    designationIds: designationIdsPayload,
                    keywords: selectedKeywords || [],
                    page: nextPage,
                    size: 10,
                    mobileNumber: mobileNumber || localStorage.getItem('userMobileNumber') || '0',
                });

                if (response?.success && response.data) {
                    const newJobs = response.data.jobs || response.data || [];
                    const transformedJobs = newJobs.map(job => {
                        let skills = job.skills || job.tags || job.requirements || 'General';
                        if (!skills || (typeof skills !== 'string' && !Array.isArray(skills))) {
                            skills = 'General';
                        }
                        return {
                            ...job,
                            id: job.id || job.jobId || Math.random().toString(36),
                            skills,
                            title: job.jobName || job.title || 'Job Title Not Available',
                            location: job.cityName && job.pincode ? `${job.cityName} - ${job.pincode}` : 'Location Not Specified',
                            salary: job.maxSalaryLimit ? `Up to ₹${job.maxSalaryLimit.toLocaleString()} per month` : 'Salary Not Disclosed'
                        };
                    });

                    const currentLanguage = selectedLanguage?.code || 'en';
                    let finalJobs = transformedJobs;
                    if (TranslationUtils.isTranslationNeeded(currentLanguage)) {
                        finalJobs = await TranslationUtils.translateJobFields(
                            transformedJobs,
                            currentLanguage,
                            ['jobName', 'jobDetails', 'cityName']
                        );
                    }

                    setJobs(prev => [...prev, ...finalJobs]);
                    setAllJobs(prev => [...prev, ...finalJobs]);
                    setHasMoreJobs(finalJobs.length === 10);
                    setCurrentPage(nextPage);
                } else {
                    setHasMoreJobs(false);
                }
            } catch (e) {
                console.error('[Homepage] loadMoreJobs (filters) error:', e);
            } finally {
                setLoadingMore(false);
                isLoadingJobsRef.current = false;
                setApiBusy(false);
            }
        } else {
            // Default coordinate-based pagination
            loadJobs(currentPage + 1, false, mobileNumber);
        }
    };

    /**
     * Handle mobile number submission    
     */
    const handleMobileSubmit = (e) => {
        e.preventDefault();
        
        // Validation based on selected country
        let isValid = false;
        let errorMessage = '';
        
        if (selectedCountry === '+91') {
            // India: 10 digits starting with 6-9
            isValid = /^[6-9]\d{9}$/.test(tempMobileNumber);
            errorMessage = 'Please enter a valid 10-digit Indian mobile number starting with 6-9';
        } else if (selectedCountry === '+971') {
            // UAE: 9 digits starting with 5
            isValid = /^5\d{8}$/.test(tempMobileNumber);
            errorMessage = 'Please enter a valid 9-digit UAE mobile number starting with 5';
        }
        
        if (!isValid) {
            setTempMobileError(errorMessage);
            return;
        }
        
        setTempMobileError('');
        if (tempMobileNumber) {
            const fullMobileNumber = `${selectedCountry}${tempMobileNumber}`;
            localStorage.setItem('userMobileNumber', fullMobileNumber);
            setMobileNumber(fullMobileNumber);
            setShowMobileModal(false);
            setTempMobileNumber('');
            loadJobs(1, true, fullMobileNumber);
        }
    };

    // Note: debounced server suggestions are handled above via serverCityOptions and mergedCityOptions

    return (
        <div>
            {null}

            <JobSearch 
                onSearch={handleSearch}
                // City search integration props
                cityOptions={serverCityOptions}
                cityQuery={cityQuery}
                setCityQuery={setCityQuery}
                showCityDropdown={showCityDropdown}
                setShowCityDropdown={setShowCityDropdown}
                isCityLoading={isCityLoading}
                isAddingCity={isAddingCity}
                selectedCities={selectedCity ? [selectedCity] : []}
                addCity={addCity}
                removeCity={removeCity}
                clearAllCities={clearAllCities}
                cityInputRef={cityInputRef}
                // Skills dropdown options from filter API
                skillsOptions={filterOptions.skills}
                selectedSkills={selectedSkills}
                addSkill={addSkill}
                removeSkill={removeSkill}
                clearAllSkills={clearAllSkills}
                // Designations (job titles) chips
                selectedDesignations={selectedDesignations}
                addDesignation={addDesignation}
                removeDesignation={removeDesignation}
                clearAllDesignations={clearAllDesignations}
                // Designation options from API
                designationOptions={filterOptions.designations}
                // Keywords (free-text search terms)
                selectedKeywords={selectedKeywords}
                addKeyword={addKeyword}
                removeKeyword={removeKeyword}
                clearAllKeywords={clearAllKeywords}
                // Clear all filters
                clearAllFilters={clearAllFilters}
                // Disable Search button while jobs are loading
                isLoadingJobs={loading || apiBusy}
            />
            
            {/* Main Content */}
            <div className="main-content" style={{
                gridTemplateColumns: '1fr 280px',
                padding: '0 1.5rem 0 1.5rem', /* Remove top padding to move cards up, 24px left/right, 0 bottom */
                maxWidth: '100%',
                boxSizing: 'border-box'
            }}>
                {/* Job Listings */}
                <main className="job-listings">
                    <FeaturedJobs 
                        onQuickApply={handleQuickApply}
                        onViewDetails={handleViewDetails}
                        onRefer={handleJobRefer}
                        jobs={jobs}
                        loading={loading}
                        apiBusy={apiBusy}
                        loadingMore={loadingMore}
                        hasLoaded={hasInitialLoadCompleted}
                        initialFetchDone={initialFetchDone}
                        error={error}
                        isSearchActive={isSearchActive}
                        currentLocation={actualJobLocation || location}
                        savedLocation={savedLocation}
                        candidateSkills={candidateSkills}
                        candidateSkillsWithRatings={candidateSkillsWithRatings}
                        skillIdToNameMap={skillIdToNameMap}
                    />
                    
                    {/* Radius-based mode: Load More button for buffered jobs */}
                    {/* Show for both normal and filtered searches (both use buffer now) */}
                    {!loading && jobs.length > 0 && isRadiusBasedMode && hasMoreJobsInBuffer && (
                        <div style={{ textAlign: 'center', marginTop: '1.5rem', marginBottom: '0' }}>
                            <button 
                                onClick={loadMoreJobs}
                                disabled={loadingMore}
                                className="load-more-btn"
                                style={{
                                    background: loadingMore ? '#9ca3af' : '#2563eb',
                                    color: 'white',
                                    padding: '0.75rem 2rem',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    cursor: loadingMore ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.3s ease',
                                    opacity: loadingMore ? 0.7 : 1
                                }}
                                onMouseOver={e => {
                                    if (!loadingMore) e.currentTarget.style.background='#1d4ed8'
                                }}
                                onMouseOut={e => {
                                    if (!loadingMore) e.currentTarget.style.background='#2563eb'
                                }}
                            >
                                {loadingMore ? (
                                    <>
                                        <div style={{
                                            display: 'inline-block',
                                            width: '16px',
                                            height: '16px',
                                            border: '2px solid #ffffff',
                                            borderRadius: '50%',
                                            borderTopColor: 'transparent',
                                            animation: 'spin 1s linear infinite',
                                            marginRight: '0.5rem'
                                        }}></div>
                                        Loading...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-plus" style={{marginRight: '0.5rem'}}></i>
                                        Load More Jobs
                                    </>
                                )}
                            </button>
                        </div>
                    )}

                    {/* Radius expansion prompt - show when all buffered jobs displayed OR during expansion */}
                    {/* Show for both normal and filtered searches (isServerFiltered can be true for filters) */}
                    {/* AND user hasn't declined expansion (hasMoreRadiusLevels must be true) */}
                    {jobs.length > 0 && isRadiusBasedMode && (allJobsInRadiusDisplayed || isExpandingRadius) && canExpandRadius && hasMoreRadiusLevels && (
                        <RadiusExpansionPrompt
                            currentRadius={promptCurrentRadius}
                            nextRadius={nextRadius}
                            onExpand={expandRadius}
                            onDecline={declineRadiusExpansion}
                            isExpanding={isExpandingRadius}
                        />
                    )}

                    {/* No more jobs message - show when all jobs displayed and no more radius levels OR not expanding */}
                    {/* Show for both normal and filtered searches */}
                    {(() => {
                        const shouldShow = !loading && jobs.length > 0 && isRadiusBasedMode && allJobsInRadiusDisplayed && !hasMoreRadiusLevels && !isExpandingRadius;
                        console.log('[Homepage] 🏁 NoMoreJobsMessage check:', {
                            shouldShow,
                            loading,
                            jobsLength: jobs.length,
                            isRadiusBasedMode,
                            allJobsInRadiusDisplayed,
                            hasMoreRadiusLevels,
                            isExpandingRadius,
                            displayedJobCount,
                            bufferLength: jobsBuffer.length,
                            hasCompletedCurrentRadiusFetch,
                            currentRadiusIndex,
                            maxRadiusIndex: radiusLevels.length - 1
                        });
                        return shouldShow ? <NoMoreJobsMessage currentRadius={currentRadius} /> : null;
                    })()}

                    {/* Server-filtered mode: Load More button for pagination (legacy - only for non-radius mode) */}
                    {!loading && jobs.length > 0 && isServerFiltered && !isRadiusBasedMode && hasMoreJobs && (
                        <div style={{ textAlign: 'center', marginTop: '1.5rem', marginBottom: '0' }}>
                            <button 
                                onClick={loadMoreJobs}
                                disabled={loadingMore}
                                className="load-more-btn"
                                style={{
                                    background: loadingMore ? '#9ca3af' : '#2563eb',
                                    color: 'white',
                                    padding: '0.75rem 2rem',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    cursor: loadingMore ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.3s ease',
                                    opacity: loadingMore ? 0.7 : 1
                                }}
                                onMouseOver={e => {
                                    if (!loadingMore) e.currentTarget.style.background='#1d4ed8'
                                }}
                                onMouseOut={e => {
                                    if (!loadingMore) e.currentTarget.style.background='#2563eb'
                                }}
                            >
                                {loadingMore ? (
                                    <>
                                        <div style={{
                                            display: 'inline-block',
                                            width: '16px',
                                            height: '16px',
                                            border: '2px solid #ffffff',
                                            borderRadius: '50%',
                                            borderTopColor: 'transparent',
                                            animation: 'spin 1s linear infinite',
                                            marginRight: '0.5rem'
                                        }}></div>
                                        Loading...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-plus" style={{marginRight: '0.5rem'}}></i>
                                        Load More Jobs
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                    
                    {/* No jobs message is handled inside FeaturedJobs to avoid duplicates */}
                    {null}
                </main>
                
                {/* Right Sidebar */}
                <aside className="right-sidebar">
                    <ReferCandidateCard />
                </aside>
            </div>
            <Footer />

            {/* Mobile Number Input Modal */}
            <Modal isOpen={showMobileModal} onClose={() => {}}>
                <form onSubmit={handleMobileSubmit}>
                    <h2>Enter Mobile Number</h2>
                    <p>Please enter your mobile number to view jobs in your area.</p>
                    <div className="form-group">
                        <label htmlFor="country">Country</label>
                        <select 
                            id="country"
                            value={selectedCountry}
                            onChange={(e) => {
                                setSelectedCountry(e.target.value);
                                setTempMobileNumber(''); // Clear mobile number when country changes
                                setTempMobileError('');
                            }}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                fontSize: '0.875rem',
                                marginBottom: '1rem'
                            }}
                        >
                            <option value="+91">🇮🇳 India (+91)</option>
                            <option value="+971">🇦🇪 UAE (+971)</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label htmlFor="mobile">Mobile Number</label>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{
                                padding: '0.75rem',
                                background: '#f3f4f6',
                                border: '1px solid #e5e7eb',
                                borderRight: 'none',
                                borderRadius: '8px 0 0 8px',
                                fontSize: '0.875rem',
                                color: '#374151'
                            }}>
                                {selectedCountry}
                            </span>
                            <input 
                                type="tel" 
                                id="mobile" 
                                value={tempMobileNumber}
                                onChange={(e) => {
                                    const maxLength = selectedCountry === '+91' ? 10 : 9;
                                    const digitsOnly = (e.target.value || '').replace(/\D/g, '').slice(0, maxLength);
                                    setTempMobileNumber(digitsOnly);
                                    if (digitsOnly.length === maxLength) {
                                        setTempMobileError('');
                                    }
                                }}
                                placeholder={selectedCountry === '+91' ? "Enter 10-digit number" : "Enter 9-digit number"}
                                inputMode="numeric"
                                maxLength={selectedCountry === '+91' ? "10" : "9"}
                                pattern={selectedCountry === '+91' ? "[0-9]{10}" : "[0-9]{9}"}
                                title={selectedCountry === '+91' ? "Enter exactly 10 digits" : "Enter exactly 9 digits"}
                                style={{
                                    flex: 1,
                                    padding: '0.75rem',
                                    border: '1px solid #e5e7eb',
                                    borderLeft: 'none',
                                    borderRadius: '0 8px 8px 0',
                                    fontSize: '0.875rem'
                                }}
                                required 
                            />
                        </div>
                        {tempMobileError && (
                            <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginTop: '0.35rem' }}>
                                {tempMobileError}
                            </div>
                        )}
                    </div>
                    <button type="submit" className="btn-apply">Continue</button>
                </form>
            </Modal>

            {/* Enhanced Apply Now Modal */}
            <ApplyNowModal 
                isOpen={isModalOpen}
                onClose={closeModal}
                job={selectedJob}
                onSuccess={handleApplySuccess}
            />



            {/* Job Details Modal */}
            <JobDetailsModal 
                isOpen={isJobDetailsModalOpen}
                onClose={closeJobDetailsModal}
                job={selectedJobForDetails}
                onRefer={handleJobRefer}
            />



            {/* Refer & Earn Modal */}
            <ReferEarnModal 
                isOpen={isReferModalOpen}
                onClose={closeReferModal}
                jobTitle={selectedJobForRefer?.jobName || selectedJobForRefer?.title}
                job={selectedJobForRefer}
                onReferralSuccess={() => {
                    console.log('Referral success callback from Homepage');
                    // Could refresh jobs or show additional feedback here
                }}
            />

            {/* Location Preference Dialog */}
            <LocationPreferenceDialog
                isOpen={showLocationPreferenceDialog}
                onClose={() => {}} // Prevent closing - user must select
                onSelectPreference={handleLocationPreferenceSelect}
                currentLocation={location}
                savedLocation={savedLocation}
            />


        </div>
    );
}

export default Homepage;
